import React, { useEffect } from 'react'
import { useController, Control } from 'react-hook-form'
import { Label, Input, HiddenCheckbox, StyledCheckbox } from '../styles/modal-style'
import CurrencyInput from '../CurrencyInput'
import InfiniteSelect from '../InfiniteSelect'
import axios from '../../config/axios'
import { getModuleById } from './Modals/integrationModulesConfig'
import IntegratedWriteCreateModal from './IntegratedWriteCreateModalComponent'

export interface IntegrationConfig {
  moduleId: string
  moduleName: string
  endpoint: string
  createEndpoint?: string
  fieldKey: string
  fieldName: string
  integrationType: 'read' | 'write'
  isRequired?: boolean
  booleanFilterValue?: boolean
  responseKey?: string
  paginationType?: 'offset' | 'page' | 'skip-take'
  writeRegistrationType?: 'collection' | 'family' | 'product' | 'fabric' | 'trim'
  requiredRegistrationFieldKeys?: string[]
  optionalRegistrationFieldKeys?: string[]
}

export interface FieldInTicket {
  ticketId?: number
  pipelineFieldId: number
  stringValue?: string | null
  bigStringValue?: string | null
  integerValue?: number | null
  decimalValue?: number | null
  dateTimeValue?: string | null
  booleanValue?: boolean | null
  pipelineField: {
    id: number
    name: string
    typeFieldId: number
    typeField?: {
      id: number
      name: string
    }
    integrationConfig?: IntegrationConfig
  }
  isRequired: boolean
}

export interface Ticket {
  id: number
  title: string
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  fieldInTickets: FieldInTicket[]
}

interface PropsInterface {
  field: FieldInTicket
  setValue: React.Dispatch<React.SetStateAction<Ticket>>
  isFilled: boolean
  control?: Control<any>
  allFieldsInTicket?: FieldInTicket[]
  isFromCurrentStep?: boolean
  writeRegistrationCreateOnSaveToken?: number
  onWriteRegistrationAutoCreateActivity?: (payload: {
    phase: 'start' | 'finish'
    token: number
    executionKey: string
  }) => void
}

interface IntegratedSelectionOption {
  id: any
  name: string
  _displayName?: string
  _swatchHex?: string
  [key: string]: any
}

interface WriteRegistrationDraftItem {
  id: string
  data: Record<string, any>
  createdAt?: string
  updatedAt?: string
}

interface WriteRegistrationDraftStore {
  version: 2
  activeDraftId: string | null
  drafts: WriteRegistrationDraftItem[]
}

interface WriteRegistrationDraftOption {
  id: string
  label: string
}

interface WriteRegistrationAutoCreateFeedback {
  id: number | string
  name: string
  viewUrl: string | null
}

const RenderField:React.FC<PropsInterface> = ({
  field,
  setValue,
  isFilled,
  allFieldsInTicket = [],
  isFromCurrentStep = true,
  writeRegistrationCreateOnSaveToken = 0,
  onWriteRegistrationAutoCreateActivity,
}) => {
  const typeId = field.pipelineField.typeFieldId
  const typeName = field.pipelineField.typeField?.name || ''
  const integrationConfig = field.pipelineField.integrationConfig
  const isIntegrated = !!integrationConfig
  
  const [sampleData, setSampleData] = React.useState<any>(null)
  const sampleDataRef = React.useRef<any>(null)
  const [searchTerm, setSearchTerm] = React.useState<string>('')
  const [integrationEndpointHasError, setIntegrationEndpointHasError] = React.useState<boolean>(false)
  const [integratedColorSwatchMap, setIntegratedColorSwatchMap] = React.useState<Record<string, string>>({})

  const writeRegistrationType = integrationConfig?.writeRegistrationType
  const canCreateRecordFromWriteIntegration = Boolean(
    integrationConfig &&
    integrationConfig.integrationType === 'write' &&
    writeRegistrationType
  )
  const [isAutoCreatingWriteRegistration, setIsAutoCreatingWriteRegistration] = React.useState(false)
  const [writeRegistrationAutoCreateFeedback, setWriteRegistrationAutoCreateFeedback] = React.useState<WriteRegistrationAutoCreateFeedback | null>(null)
  const autoCreateInFlightKeysRef = React.useRef<Set<string>>(new Set())
  const autoCreateCompletedKeysRef = React.useRef<Set<string>>(new Set())
  const lastHandledWriteRegistrationCreateOnSaveTokenRef = React.useRef<number>(0)

  const writeRegistrationDefaultRequiredKeys = React.useMemo(() => ({
    collection: ['name', 'collectionColors', 'gradeIds', 'materials'],
    family: ['name', 'collectionId', 'collectionColorIds', 'gradeIds', 'materials'],
    product: ['productCode', 'name', 'collectionId', 'familyId', 'groupId', 'gradeIds', 'compositionItems'],
    fabric: ['code', 'material', 'color', 'pantoneCode'],
    trim: ['name', 'categoryId', 'materialId', 'colors'],
  }), [])

  const createWriteRegistrationDraftId = React.useCallback((): string => {
    const timestamp = Date.now().toString(36)
    const randomSuffix = Math.random().toString(36).slice(2, 8)
    return `draft-${timestamp}-${randomSuffix}`
  }, [])

  const normalizeWriteRegistrationDraftData = React.useCallback((rawDraftData: Record<string, any>): Record<string, any> => {
    const normalizedDraftData: Record<string, any> = {}

    Object.keys(rawDraftData || {}).forEach((key) => {
      const value = rawDraftData[key]
      if (value === undefined || value === null) return
      if (typeof value === 'string' && value.trim() === '') return
      if (Array.isArray(value) && value.length === 0) return
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return
      normalizedDraftData[key] = value
    })

    return normalizedDraftData
  }, [])

  const parseWriteRegistrationDraftStore = React.useCallback((rawValue: unknown): WriteRegistrationDraftStore => {
    const emptyStore: WriteRegistrationDraftStore = {
      version: 2,
      activeDraftId: null,
      drafts: [],
    }

    if (typeof rawValue !== 'string' || rawValue.trim() === '') return emptyStore

    try {
      const parsed = JSON.parse(rawValue)

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return emptyStore

      const parsedAsStore = parsed as WriteRegistrationDraftStore
      if (parsedAsStore.version === 2 && Array.isArray(parsedAsStore.drafts)) {
        const normalizedDrafts = parsedAsStore.drafts
          .filter((draft) => draft && typeof draft === 'object' && typeof draft.id === 'string')
          .map((draft) => ({
            id: String(draft.id),
            data: normalizeWriteRegistrationDraftData(draft.data || {}),
            createdAt: draft.createdAt,
            updatedAt: draft.updatedAt,
          }))

        const normalizedActiveDraftId = typeof parsedAsStore.activeDraftId === 'string' && parsedAsStore.activeDraftId.trim() !== ''
          ? parsedAsStore.activeDraftId
          : null
        return {
          version: 2,
          activeDraftId: normalizedActiveDraftId || (normalizedDrafts[0]?.id || null),
          drafts: normalizedDrafts,
        }
      }

      const normalizedLegacyDraft = normalizeWriteRegistrationDraftData(parsed as Record<string, any>)
      if (Object.keys(normalizedLegacyDraft).length === 0) return emptyStore

      return {
        version: 2,
        activeDraftId: 'legacy',
        drafts: [{
          id: 'legacy',
          data: normalizedLegacyDraft,
        }],
      }
    } catch {
      return emptyStore
    }
  }, [normalizeWriteRegistrationDraftData])

  const serializeWriteRegistrationDraftStore = React.useCallback((draftStore: WriteRegistrationDraftStore): string | undefined => {
    const normalizedDrafts = (draftStore.drafts || [])
      .filter((draftItem) => draftItem && typeof draftItem.id === 'string' && String(draftItem.id).trim() !== '')
      .map((draftItem) => ({
        id: String(draftItem.id),
        data: normalizeWriteRegistrationDraftData(draftItem.data || {}),
        createdAt: draftItem.createdAt,
        updatedAt: draftItem.updatedAt,
      }))

    if (normalizedDrafts.length === 0) return undefined

    const hasValidActiveDraft = normalizedDrafts.some((draftItem) => draftItem.id === draftStore.activeDraftId)
    const normalizedStore: WriteRegistrationDraftStore = {
      version: 2,
      activeDraftId: hasValidActiveDraft ? draftStore.activeDraftId : normalizedDrafts[0].id,
      drafts: normalizedDrafts,
    }

    return JSON.stringify(normalizedStore)
  }, [normalizeWriteRegistrationDraftData])

  const isSameWriteRegistrationGroup = React.useCallback((candidateField: FieldInTicket): boolean => {
    if (!integrationConfig || integrationConfig.integrationType !== 'write') return false
    const candidateConfig = candidateField.pipelineField?.integrationConfig
    if (!candidateConfig || candidateConfig.integrationType !== 'write') return false

    if (integrationConfig.writeRegistrationType && candidateConfig.writeRegistrationType) {
      return integrationConfig.writeRegistrationType === candidateConfig.writeRegistrationType
    }

    return String(integrationConfig.moduleId || '') === String(candidateConfig.moduleId || '')
  }, [integrationConfig])

  const writeRegistrationFieldGroup = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return []
    const sourceFields = allFieldsInTicket.length > 0 ? allFieldsInTicket : [field]
    return sourceFields.filter((candidateField) => isSameWriteRegistrationGroup(candidateField))
  }, [allFieldsInTicket, canCreateRecordFromWriteIntegration, field, isSameWriteRegistrationGroup])

  const writeRegistrationDraftStoresByField = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return []

    return writeRegistrationFieldGroup.map((groupField) => ({
      pipelineFieldId: Number(groupField.pipelineFieldId),
      draftStore: parseWriteRegistrationDraftStore(groupField.bigStringValue),
    }))
  }, [canCreateRecordFromWriteIntegration, parseWriteRegistrationDraftStore, writeRegistrationFieldGroup])

  const currentFieldPreferredDraftId = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return null
    const currentStore = parseWriteRegistrationDraftStore(field.bigStringValue)
    if (currentStore.activeDraftId) return currentStore.activeDraftId
    return currentStore.drafts[0]?.id || null
  }, [canCreateRecordFromWriteIntegration, field.bigStringValue, parseWriteRegistrationDraftStore])

  const activeWriteRegistrationDraftId = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return null

    if (currentFieldPreferredDraftId) {
      return currentFieldPreferredDraftId
    }

    for (const item of writeRegistrationDraftStoresByField) {
      const activeDraftId = item.draftStore.activeDraftId
      if (activeDraftId && item.draftStore.drafts.some((draftItem) => draftItem.id === activeDraftId)) {
        return activeDraftId
      }
    }

    for (const item of writeRegistrationDraftStoresByField) {
      if (item.draftStore.drafts.length > 0) {
        return item.draftStore.drafts[0].id
      }
    }

    return null
  }, [canCreateRecordFromWriteIntegration, currentFieldPreferredDraftId, writeRegistrationDraftStoresByField])

  const currentFieldWriteRegistrationDraftStore = React.useMemo(() => {
    return parseWriteRegistrationDraftStore(field.bigStringValue)
  }, [field.bigStringValue, parseWriteRegistrationDraftStore])

  const currentWriteRegistrationStageDraft = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return {}
    if (!activeWriteRegistrationDraftId) return {}

    const currentDraft = currentFieldWriteRegistrationDraftStore.drafts.find((draftItem) => draftItem.id === activeWriteRegistrationDraftId)
    return currentDraft?.data || {}
  }, [activeWriteRegistrationDraftId, canCreateRecordFromWriteIntegration, currentFieldWriteRegistrationDraftStore.drafts])

  const writeRegistrationDraftOptions = React.useMemo((): WriteRegistrationDraftOption[] => {
    if (!canCreateRecordFromWriteIntegration) return []

    const mergedDraftMap = new Map<string, WriteRegistrationDraftItem>()

    writeRegistrationDraftStoresByField.forEach((item) => {
      item.draftStore.drafts.forEach((draftItem) => {
        const existingDraft = mergedDraftMap.get(draftItem.id)
        if (!existingDraft) {
          mergedDraftMap.set(draftItem.id, draftItem)
          return
        }

        const existingDataSize = Object.keys(existingDraft.data || {}).length
        const incomingDataSize = Object.keys(draftItem.data || {}).length
        if (incomingDataSize >= existingDataSize) {
          mergedDraftMap.set(draftItem.id, {
            ...existingDraft,
            ...draftItem,
            data: {
              ...existingDraft.data,
              ...draftItem.data,
            },
          })
        }
      })
    })

    const orderedDrafts = Array.from(mergedDraftMap.values())

    return orderedDrafts.map((draftItem, index) => {
      const draftData = draftItem.data || {}
      const labelCandidate = String(
        draftData.name ||
        draftData.productCode ||
        draftData.code ||
        draftData.color ||
        ''
      ).trim()

      const labelPrefix = labelCandidate ? labelCandidate : `Item ${index + 1}`
      return {
        id: draftItem.id,
        label: `${labelPrefix}`,
      }
    })
  }, [canCreateRecordFromWriteIntegration, writeRegistrationDraftStoresByField])

  const aggregatedWriteRegistrationDraft = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return {}
    if (!activeWriteRegistrationDraftId) return {}

    const mergedDraft: Record<string, any> = {}

    writeRegistrationDraftStoresByField.forEach((item) => {
      const activeDraft = item.draftStore.drafts.find((draftItem) => draftItem.id === activeWriteRegistrationDraftId)
      if (!activeDraft) return

      Object.keys(activeDraft.data).forEach((key) => {
        const value = activeDraft.data[key]
        if (value === undefined || value === null) return
        if (typeof value === 'string' && value.trim() === '') return
        mergedDraft[key] = value
      })
    })

    return mergedDraft
  }, [activeWriteRegistrationDraftId, canCreateRecordFromWriteIntegration, writeRegistrationDraftStoresByField])

  const stagedWriteRegistrationEnabled = React.useMemo(() => {
    if (!canCreateRecordFromWriteIntegration) return false
    const configuredKeys = integrationConfig?.requiredRegistrationFieldKeys
    return Array.isArray(configuredKeys) && configuredKeys.length > 0
  }, [canCreateRecordFromWriteIntegration, integrationConfig?.requiredRegistrationFieldKeys])

  const stageDraftForInlineWriteRegistration = React.useMemo(() => {
    if (!stagedWriteRegistrationEnabled) return currentWriteRegistrationStageDraft
    return normalizeWriteRegistrationDraftData({
      ...aggregatedWriteRegistrationDraft,
      ...currentWriteRegistrationStageDraft,
    })
  }, [aggregatedWriteRegistrationDraft, currentWriteRegistrationStageDraft, normalizeWriteRegistrationDraftData, stagedWriteRegistrationEnabled])

  useEffect(() => {
    if (!canCreateRecordFromWriteIntegration) return

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] draft-sync-snapshot', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      stagedWriteRegistrationEnabled,
      activeWriteRegistrationDraftId,
      currentFieldActiveDraftId: currentFieldWriteRegistrationDraftStore.activeDraftId,
      currentFieldDraftIds: currentFieldWriteRegistrationDraftStore.drafts.map((draftItem) => draftItem.id),
      draftOptionIds: writeRegistrationDraftOptions.map((draftOption) => draftOption.id),
      stageDraftKeys: Object.keys(stageDraftForInlineWriteRegistration || {}),
      currentFieldStageDraftKeys: Object.keys(currentWriteRegistrationStageDraft || {}),
      aggregatedDraftKeys: Object.keys(aggregatedWriteRegistrationDraft || {}),
      draftStoresByField: writeRegistrationDraftStoresByField.map((item) => ({
        pipelineFieldId: item.pipelineFieldId,
        activeDraftId: item.draftStore.activeDraftId,
        draftIds: item.draftStore.drafts.map((draftItem) => draftItem.id),
      })),
    })
  }, [activeWriteRegistrationDraftId, aggregatedWriteRegistrationDraft, canCreateRecordFromWriteIntegration, currentFieldWriteRegistrationDraftStore.activeDraftId, currentFieldWriteRegistrationDraftStore.drafts, currentWriteRegistrationStageDraft, field.pipelineFieldId, stageDraftForInlineWriteRegistration, stagedWriteRegistrationEnabled, writeRegistrationDraftOptions, writeRegistrationDraftStoresByField, writeRegistrationType])

  const requiredRegistrationFieldKeys = React.useMemo(() => {
    if (!writeRegistrationType) return []
    const configuredKeys = integrationConfig?.requiredRegistrationFieldKeys || []
    if (Array.isArray(configuredKeys) && configuredKeys.length > 0) return configuredKeys
    return writeRegistrationDefaultRequiredKeys[writeRegistrationType] || []
  }, [integrationConfig?.requiredRegistrationFieldKeys, writeRegistrationDefaultRequiredKeys, writeRegistrationType])

  const normalizeColorLabel = React.useCallback((value: any): string => {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
  }, [])

  const isNumericChipLabel = React.useCallback((value: any): boolean => {
    const normalizedValue = String(value || '').trim()
    return /^\d+$/.test(normalizedValue)
  }, [])

  const isMeaningfulValue = React.useCallback((value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized !== '' && normalized !== 'null' && normalized !== 'undefined'
    }
    if (Array.isArray(value)) {
      return value.some((item) => isMeaningfulValue(item))
    }
    if (typeof value === 'object') {
      const objectCandidates = [
        value.name,
        value.typeName,
        value.companyName,
        value.description,
        value.code,
        value.id,
      ]
      return objectCandidates.some((candidate) => isMeaningfulValue(candidate))
    }
    return true
  }, [])

  const getNormalizedIntegrationFieldKey = React.useCallback((fieldKey: string): string => {
    if (!integrationConfig) return fieldKey
    if (integrationConfig.moduleId !== 'companies') return fieldKey
    if (fieldKey === 'City.name') return 'city.name'
    if (fieldKey === 'State.name') return 'state.name'
    return fieldKey
  }, [integrationConfig])

  const isIntegratedColorField = React.useMemo(() => {
    if (!isIntegrated || !integrationConfig) return false
    const normalizedFieldKey = normalizeColorLabel(getNormalizedIntegrationFieldKey(integrationConfig.fieldKey || ''))
    const normalizedFieldName = normalizeColorLabel(field.pipelineField.name || '')
    return normalizedFieldKey.includes('color') || normalizedFieldKey.includes('cor') || normalizedFieldName.includes(' cor') || normalizedFieldName.endsWith('cor') || normalizedFieldName.endsWith('cores')
  }, [field.pipelineField.name, getNormalizedIntegrationFieldKey, integrationConfig, isIntegrated, normalizeColorLabel])
  
  const getNestedValue = React.useCallback((obj: any, path: string): any => {
    const keys = path.split('.')
    let val = obj
    for (const k of keys) {
      if (val === null || val === undefined) {
        return null
      }
      if (Array.isArray(val)) {
        const nextValues = val
          .map((item) => item?.[k])
          .filter((item) => item !== null && item !== undefined)
          .flatMap((item) => Array.isArray(item) ? item : [item])
        if (nextValues.length === 0) {
          return null
        }
        val = nextValues
        continue
      }
      val = val[k]
    }
    return val
  }, [])

  const getFieldKeyCandidates = React.useCallback((fieldKey: string): string[] => {
    const normalizedFieldKey = getNormalizedIntegrationFieldKey(fieldKey)
    const candidates = new Set<string>([normalizedFieldKey])

    if (integrationConfig?.moduleId === 'companies') {
      const segments = normalizedFieldKey.split('.')

      if (segments.length > 0) {
        const root = segments[0]
        const rootCandidates = [
          root,
          `${root.charAt(0).toUpperCase()}${root.slice(1)}`,
          `${root.charAt(0).toLowerCase()}${root.slice(1)}`
        ]

        if (segments.length > 1) {
          rootCandidates.forEach((rootVariant) => {
            candidates.add([rootVariant, ...segments.slice(1)].join('.'))
            candidates.add(rootVariant)
          })
        } else {
          rootCandidates.forEach((rootVariant) => candidates.add(rootVariant))
        }
      }
    }

    return Array.from(candidates)
  }, [getNormalizedIntegrationFieldKey, integrationConfig])

  const getFieldValueWithFallback = React.useCallback((item: any, fieldKey: string) => {
    const candidates = getFieldKeyCandidates(fieldKey)

    for (const candidate of candidates) {
      const value = getNestedValue(item, candidate)
      if (isMeaningfulValue(value)) {
        return value
      }
      if (value === 0 || value === false) {
        return value
      }
    }

    return null
  }, [getFieldKeyCandidates, getNestedValue, isMeaningfulValue])
  
  const hasValidScalarValue = React.useCallback((item: any, fieldKey: string): boolean => {
    const value = getFieldValueWithFallback(item, fieldKey)
    if (!isMeaningfulValue(value)) return false
    if (typeof value === 'number' && value === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      const objName = value.name || value.typeName || value.companyName || value.id
      return isMeaningfulValue(objName)
    }
    return true
  }, [getFieldValueWithFallback, isMeaningfulValue])
  
  const extractDisplayName = React.useCallback((v: any): string => {
    if (!isMeaningfulValue(v)) return ''
    if (typeof v !== 'object' || v === null) return String(v)
    if (isMeaningfulValue(v.name)) return String(v.name)
    if (isMeaningfulValue(v.typeName)) return String(v.typeName)
    if (isMeaningfulValue(v.companyName)) return String(v.companyName)
    const nestedKeys = ['Grade', 'Group', 'Material', 'CollectionColor', 'ItemColor', 'Stage', 'Resource', 'Stock']
    for (const key of nestedKeys) {
      if (v[key] && typeof v[key] === 'object') {
        const inner = v[key]
        if (isMeaningfulValue(inner.name)) return String(inner.name)
        if (isMeaningfulValue(inner.description)) return String(inner.description)
      }
    }
    return isMeaningfulValue(v.id) ? String(v.id) : ''
  }, [isMeaningfulValue])

  const getDisplayValueFromField = React.useCallback((item: any, fieldKey: string): string => {
    const value = getFieldValueWithFallback(item, fieldKey)
    
    if (!isMeaningfulValue(value)) return ''
    if (Array.isArray(value)) {
      const names = value.map(v => extractDisplayName(v)).filter((name) => isMeaningfulValue(name))
      if (names.length === 0) return ''
      if (names.length <= 3) return names.join(', ')
      return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
    }

    const normalizedFieldKey = String(fieldKey || '').trim().toLowerCase()
    const hasBooleanFilterConfigured = typeof integrationConfig?.booleanFilterValue === 'boolean'
    const isStockActivationField = normalizedFieldKey === 'isactivated' || (normalizedFieldKey === 'status' && hasBooleanFilterConfigured)
    if (isStockActivationField) {
      const resolveBooleanCandidate = () => {
        if (typeof value === 'boolean') return value
        if ((value === null || value === undefined || value === '') && item) {
          const fallbackIsActivated = getFieldValueWithFallback(item, 'isActivated')
          if (typeof fallbackIsActivated === 'boolean') return fallbackIsActivated
          if (typeof fallbackIsActivated === 'number') {
            if (fallbackIsActivated === 1) return true
            if (fallbackIsActivated === 0) return false
          }
          if (typeof fallbackIsActivated === 'string') {
            const normalizedFallback = fallbackIsActivated.trim().toLowerCase()
            if (normalizedFallback === 'true' || normalizedFallback === '1' || normalizedFallback === 'ativo' || normalizedFallback === 'active') return true
            if (normalizedFallback === 'false' || normalizedFallback === '0' || normalizedFallback === 'inativo' || normalizedFallback === 'inactive') return false
          }
        }
        return value
      }

      const booleanCandidate = resolveBooleanCandidate()

      if (typeof booleanCandidate === 'boolean') {
        return booleanCandidate ? 'Ativo' : 'Inativo'
      }
      if (typeof booleanCandidate === 'number') {
        if (booleanCandidate === 1) return 'Ativo'
        if (booleanCandidate === 0) return 'Inativo'
      }
      if (typeof booleanCandidate === 'string') {
        const normalizedValue = booleanCandidate.trim().toLowerCase()
        if (normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'ativo') return 'Ativo'
        if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'inativo') return 'Inativo'
      }
    }

    if (typeof value === 'object') {
      const objectDisplay = value.name || value.typeName || value.companyName || value.id
      return isMeaningfulValue(objectDisplay) ? String(objectDisplay) : ''
    }
    const textValue = String(value)
    return isMeaningfulValue(textValue) ? textValue : ''
  }, [getFieldValueWithFallback, extractDisplayName, isMeaningfulValue, integrationConfig])

  const getSwatchHex = React.useCallback((value: any): string | undefined => {
    const normalizeHexCandidate = (candidate: any): string | undefined => {
      if (typeof candidate !== 'string') return undefined
      const trimmed = candidate.trim()
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return trimmed
      if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return `#${trimmed}`
      return undefined
    }

    const visited = new Set<any>()

    const findHexRecursively = (input: any, depth: number): string | undefined => {
      if (depth > 6) return undefined

      const directHex = normalizeHexCandidate(input)
      if (directHex) return directHex

      if (!input || typeof input !== 'object') return undefined
      if (visited.has(input)) return undefined
      visited.add(input)

      if (Array.isArray(input)) {
        for (const item of input) {
          const nestedHex = findHexRecursively(item, depth + 1)
          if (nestedHex) return nestedHex
        }
        return undefined
      }

      const prioritizedKeys = [
        '_swatchHex',
        'hex',
        'Hex',
        'pantone',
        'Pantone',
        'color',
        'Color',
        'itemColor',
        'ItemColor',
        'collectionColor',
        'CollectionColor',
      ]

      for (const key of prioritizedKeys) {
        if (!(key in input)) continue
        const nestedHex = findHexRecursively(input[key], depth + 1)
        if (nestedHex) return nestedHex
      }

      for (const nestedValue of Object.values(input)) {
        const nestedHex = findHexRecursively(nestedValue, depth + 1)
        if (nestedHex) return nestedHex
      }

      return undefined
    }

    return findHexRecursively(value, 0)
  }, [])
  
  useEffect(() => {
    const fetchIntegrationDataOnMount = async () => {
      if (!isIntegrated || !integrationConfig || !field.integerValue || (field as any).integrationData) return
      
      try {
        const moduleConfig = getModuleById(integrationConfig.moduleId)
        const resolvedEndpoint = moduleConfig?.endpoint || integrationConfig.endpoint
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}${resolvedEndpoint}/${field.integerValue}`
        )
        const data = response.data?.data || response.data
        
        if (data) {
          setSampleData(data)
          setValue(prev => ({
            ...prev,
            fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
              ...f,
              integrationData: data
            } : f)
          }))
        }
      } catch (error) {
        console.error('Erro ao buscar dados integrados:', error)
      }
    }
    
    fetchIntegrationDataOnMount()
  }, [field.integerValue, isIntegrated])
  
  const isEmail = typeName === 'E-mail'
  const isCpf = typeName === 'CPF'
  const isCnpj = typeName === 'CNPJ'
  const isDate = typeName === 'Data'
  const isCheckbox = typeName === 'Checkbox'
  const isCelular = typeName === 'Celular'
  const isTelefone = typeName === 'Telefone'
  const isTexto = typeName === 'Texto simples'
  const isValor = typeName === 'Valor (R$)'
  const isString = isEmail || isCpf || isCnpj || isCelular || isTelefone || isTexto

  const maskCpf = (v: string) => v.replace(/\D/g, '').slice(0,11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  const maskCnpj = (v: string) => v.replace(/\D/g, '').slice(0,14).replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2')
  const maskPhone = (v: string) => {
    const digits = v.replace(/\D/g, '')
    if (isCelular) {
      const sliced = digits.slice(0,11)
      if (sliced.length <= 10) return sliced.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : `(${a}`)
      return sliced.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : `(${a}) ${b}`)
    }
    const sliced = digits.slice(0,10)
    return sliced.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : `(${a}`)
  }

  const placeholder = isTexto ? 'Digite aqui' : 'Digite...'

  const handleStringChange = (raw: string) => {
    let formatted = raw
    if (isCpf) formatted = maskCpf(raw)
    if (isCnpj) formatted = maskCnpj(raw)
    if (isCelular || isTelefone) formatted = maskPhone(raw)

    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        stringValue: formatted
      } : f)
    }))
  }

  const handleDecimalChange = (_value: string | undefined, _name?: string, values?: any) => {
    const floatValue = values?.float
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        decimalValue: floatValue
      } : f)
    }))
  }

  const handleDateChange = (value: string) => {
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        dateTimeValue: value
      } : f)
    }))
  }

  const handleToggle = () => {
    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => f.pipelineFieldId === field.pipelineFieldId ? {
        ...f,
        booleanValue: !f.booleanValue
      } : f)
    }))
  }

  const getPrimaryLabel = React.useCallback((option: any): string => {
    if (!option) return ''
    const label = option.name || option.description || option.companyName || option.productCode || option.codeOfOP || option.plate || ''
    const normalizedLabel = isMeaningfulValue(label) ? String(label) : ''
    const fallbackId = isMeaningfulValue(option?.id) ? String(option.id) : ''
    console.log('[getPrimaryLabel] option.id:', option?.id, 'label:', normalizedLabel || fallbackId, 'option:', option)
    return normalizedLabel || fallbackId
  }, [isMeaningfulValue])

  const getIntegratedSelectedOptions = React.useCallback((): IntegratedSelectionOption[] => {
    const normalizeIdKey = (value: any): string => String(value)
    const isNumericId = (value: any): boolean => Number.isFinite(Number(value))

    const normalizeOption = (option: any): IntegratedSelectionOption | null => {
      if (!option) return null
      const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig?.fieldKey || 'name')
      const derivedDisplayName = getDisplayValueFromField(option, fieldKey) || getPrimaryLabel(option)
      const rawName = option.name || option._displayName || option.stringValue || option.label || derivedDisplayName || ''
      const optionName = isMeaningfulValue(rawName) ? String(rawName).trim() : ''

      const optionId = option.id ?? option.integerValue
      let normalizedId: any = optionId
      if (optionId === null || optionId === undefined || optionId === '') {
        if (!optionName) return null
        normalizedId = `name-${optionName.toLowerCase()}`
      } else if (isNumericId(optionId)) {
        normalizedId = Number(optionId)
      } else {
        normalizedId = String(optionId)
      }

      return {
        ...option,
        id: normalizedId,
        name: optionName || String(normalizedId),
      }
    }

    const selectedOptions: IntegratedSelectionOption[] = []
    const isWriteIntegrationField = integrationConfig?.integrationType === 'write'
    const hasExplicitIntegrationSelectionsArray = Array.isArray((field as any).integrationDataSelections)

    const selectionsFromState = (field as any).integrationDataSelections
    if (Array.isArray(selectionsFromState) && selectionsFromState.length > 0) {
      selectionsFromState.forEach((item: any) => {
        const normalized = normalizeOption(item)
        if (normalized) selectedOptions.push(normalized)
      })
    }

    const selectionFromIntegrationData = normalizeOption((field as any).integrationData)
    if (selectionFromIntegrationData) {
      selectedOptions.push(selectionFromIntegrationData)
    }

    if ((!isWriteIntegrationField || !hasExplicitIntegrationSelectionsArray) && isMeaningfulValue(field.stringValue)) {
      const parsedNames = String(field.stringValue)
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '')

      parsedNames.forEach((name, index) => {
        const optionId = index === 0 && field.integerValue !== null && field.integerValue !== undefined
          ? Number(field.integerValue)
          : -100000 - index

        selectedOptions.push({
          id: optionId,
          name,
        })
      })
    }

    if ((!isWriteIntegrationField || !hasExplicitIntegrationSelectionsArray) && selectedOptions.length === 0 && field.integerValue) {
      const fallbackName = isMeaningfulValue(field.stringValue) ? String(field.stringValue) : String(field.integerValue)
      selectedOptions.push({
        id: Number(field.integerValue),
        name: fallbackName,
      })
    }

    const normalizedSelectedOptions = isIntegratedColorField
      ? (() => {
          const idsWithDescriptiveName = new Set<string>()
          selectedOptions.forEach((option) => {
            if (!isNumericId(option.id)) return
            if (!isNumericChipLabel(option.name) && isMeaningfulValue(option.name)) {
              idsWithDescriptiveName.add(normalizeIdKey(option.id))
            }
          })

          return selectedOptions.filter((option) => {
            if (!isNumericId(option.id)) return true
            if (!idsWithDescriptiveName.has(normalizeIdKey(option.id))) return true
            return !isNumericChipLabel(option.name)
          })
        })()
      : selectedOptions

    const uniqueByName = new Map<string, IntegratedSelectionOption>()
    const uniqueById = new Map<string, IntegratedSelectionOption>()
    normalizedSelectedOptions.forEach((option) => {
      const normalizedNameKey = String(option.name || '').trim().toLowerCase()
      const normalizedIdKey = normalizeIdKey(option.id)
      const existingByName = uniqueByName.get(normalizedNameKey)

      if (!normalizedNameKey) {
        if (!uniqueById.has(normalizedIdKey)) {
          uniqueById.set(normalizedIdKey, option)
        }
        return
      }

      if (!existingByName) {
        uniqueByName.set(normalizedNameKey, option)
        uniqueById.set(normalizedIdKey, option)
        return
      }

      const shouldReplaceByName = !isNumericId(existingByName.id) && isNumericId(option.id)
      if (shouldReplaceByName) {
        uniqueByName.set(normalizedNameKey, option)
      }
    })

    const mergedSelections = Array.from(uniqueByName.values())
    console.debug('[PIPELINE-INTEGRATED-MULTI] getIntegratedSelectedOptions', {
      pipelineFieldId: field.pipelineFieldId,
      sourceSelectionsFromStateCount: Array.isArray((field as any).integrationDataSelections) ? (field as any).integrationDataSelections.length : 0,
      sourceStringValue: field.stringValue,
      sourceIntegerValue: field.integerValue,
      sourceIntegrationDataId: (field as any).integrationData?.id,
      rawSelectedOptions: selectedOptions.map((option) => ({ id: option.id, name: option.name })),
      normalizedSelectedOptions: normalizedSelectedOptions.map((option) => ({ id: option.id, name: option.name })),
      mergedSelections: mergedSelections.map((option) => ({ id: option.id, name: option.name }))
    })

    return mergedSelections
  }, [field, field.integerValue, field.stringValue, getDisplayValueFromField, getNormalizedIntegrationFieldKey, getPrimaryLabel, integrationConfig?.fieldKey, integrationConfig?.integrationType, isIntegratedColorField, isMeaningfulValue, isNumericChipLabel, normalizeColorLabel])

  const persistIntegratedSelectedOptions = React.useCallback((selectedOptions: IntegratedSelectionOption[]) => {
    const getNumericId = (value: any): number | null => {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : null
    }

    const cleanedSelections = selectedOptions
      .filter((option) => option && option.id !== null && option.id !== undefined)
      .map((option) => {
        const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig?.fieldKey || 'name')
        const resolvedName = option._displayName || option.name || getDisplayValueFromField(option, fieldKey) || getPrimaryLabel(option)
        const optionName = isMeaningfulValue(resolvedName) ? String(resolvedName) : String(option.id)
        return {
          ...option,
          id: option.id,
          name: optionName,
        }
      })

    const primaryOption = cleanedSelections[0]
    const primaryOptionName = primaryOption?.name
    const firstSelectionWithNumericId = cleanedSelections.find((option) => getNumericId(option.id) !== null) || null
    const numericReferenceFromSelection = firstSelectionWithNumericId ? getNumericId(firstSelectionWithNumericId.id) : null
    const fallbackNumericReference = field.integerValue !== null && field.integerValue !== undefined ? Number(field.integerValue) : null
    const persistedIntegerValue = cleanedSelections.length > 0
      ? (numericReferenceFromSelection ?? fallbackNumericReference ?? null)
      : null
    const persistedStringValue = cleanedSelections.length > 0
      ? cleanedSelections.map((option) => option.name).join(', ')
      : null

    console.debug('[PIPELINE-INTEGRATED-MULTI] persistIntegratedSelectedOptions', {
      pipelineFieldId: field.pipelineFieldId,
      incomingSelections: selectedOptions.map((option) => ({ id: option.id, name: option.name })),
      cleanedSelections: cleanedSelections.map((option) => ({ id: option.id, name: option.name })),
      persistedIntegerValue,
      persistedStringValue
    })

    setValue(prev => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map(f => {
        const shouldSyncField = canCreateRecordFromWriteIntegration
          ? isSameWriteRegistrationGroup(f)
          : f.pipelineFieldId === field.pipelineFieldId

        if (!shouldSyncField) return f

        return {
          ...f,
          integerValue: persistedIntegerValue,
          stringValue: persistedStringValue,
          bigStringValue: cleanedSelections.length > 0 ? undefined : null,
          integrationData: firstSelectionWithNumericId || primaryOption || null,
          integrationDataSelections: cleanedSelections
        }
      })
    }))
  }, [canCreateRecordFromWriteIntegration, field.integerValue, field.pipelineFieldId, getDisplayValueFromField, getNormalizedIntegrationFieldKey, getPrimaryLabel, getSwatchHex, integrationConfig?.fieldKey, isMeaningfulValue, isSameWriteRegistrationGroup, setValue])

  const handleIntegrationSelect = (option: any | null) => {
    console.log('[RenderField] handleIntegrationSelect chamado → option:', option)
    if (!option) return

    const currentSelections = getIntegratedSelectedOptions()
    const parsedOptionId = Number(option.id)
    const optionId = Number.isFinite(parsedOptionId) ? parsedOptionId : String(option.id)
    const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig?.fieldKey || 'name')
    const displayValue = option._displayName || getDisplayValueFromField(option, fieldKey) || getPrimaryLabel(option)
    const normalizedDisplayValue = String(displayValue || '').trim().toLowerCase()

    const alreadySelected = currentSelections.some((selectedOption) => {
      const selectedParsedId = Number(selectedOption.id)
      const normalizedSelectedId = Number.isFinite(selectedParsedId)
        ? selectedParsedId
        : String(selectedOption.id)
      const sameId = normalizedSelectedId === optionId
      const sameName = String(selectedOption.name || '').trim().toLowerCase() === normalizedDisplayValue
      return sameId || (normalizedDisplayValue !== '' && sameName)
    })

    if (alreadySelected) {
      return
    }

    const nextSelections: IntegratedSelectionOption[] = [
      ...currentSelections,
      {
        ...option,
        id: optionId,
        name: String(displayValue || optionId),
      }
    ]

    console.log('[RenderField] handleIntegrationSelect múltiplo → nextSelections:', nextSelections.map((selectedOption) => ({ id: selectedOption.id, name: selectedOption.name })))
    persistIntegratedSelectedOptions(nextSelections)
  }

  const handleRemoveIntegratedSelection = React.useCallback((optionId: any) => {
    const normalizeIdKey = (value: any): string => String(value)
    const currentSelections = getIntegratedSelectedOptions()
    const nextSelections = currentSelections.filter((selectedOption) => normalizeIdKey(selectedOption.id) !== normalizeIdKey(optionId))

    console.debug('[PIPELINE-INTEGRATED-MULTI] handleRemoveIntegratedSelection', {
      pipelineFieldId: field.pipelineFieldId,
      removedOptionId: optionId,
      beforeCount: currentSelections.length,
      afterCount: nextSelections.length
    })

    persistIntegratedSelectedOptions(nextSelections)
  }, [field.pipelineFieldId, getIntegratedSelectedOptions, persistIntegratedSelectedOptions])

  const integratedSelectedOptions = React.useMemo(() => getIntegratedSelectedOptions(), [getIntegratedSelectedOptions])

  const persistWriteRegistrationStageDraft = React.useCallback((stageDraft: Record<string, any>) => {
    if (!stagedWriteRegistrationEnabled) return

    const normalizedStageDraft = normalizeWriteRegistrationDraftData(stageDraft || {})
    const draftId = activeWriteRegistrationDraftId || createWriteRegistrationDraftId()
    const nowIsoString = new Date().toISOString()

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-request', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      draftId,
      incomingStageDraftKeys: Object.keys(stageDraft || {}),
      normalizedStageDraftKeys: Object.keys(normalizedStageDraft),
      activeWriteRegistrationDraftId,
    })

    setValue((prev) => {
      let hasChanges = false

      const nextFieldInTickets = prev.fieldInTickets.map((fieldInTicket) => {
        if (Number(fieldInTicket.pipelineFieldId) !== Number(field.pipelineFieldId)) return fieldInTicket

        const currentDraftStore = parseWriteRegistrationDraftStore(fieldInTicket.bigStringValue)
        const existingDraftIndex = currentDraftStore.drafts.findIndex((draftItem) => draftItem.id === draftId)
        const nextDraftItems = [...currentDraftStore.drafts]

        if (Object.keys(normalizedStageDraft).length === 0) {
          console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-empty-payload-skip', {
            pipelineFieldId: Number(field.pipelineFieldId),
            draftId,
            writeRegistrationType,
            currentDraftCount: currentDraftStore.drafts.length,
          })
          return fieldInTicket
        } else if (existingDraftIndex >= 0) {
          const existingDraft = nextDraftItems[existingDraftIndex]
          const mergedDraftData = normalizeWriteRegistrationDraftData({
            ...(existingDraft.data || {}),
            ...normalizedStageDraft,
          })
          nextDraftItems[existingDraftIndex] = {
            ...existingDraft,
            data: mergedDraftData,
            updatedAt: nowIsoString,
          }
        } else {
          nextDraftItems.push({
            id: draftId,
            data: normalizedStageDraft,
            createdAt: nowIsoString,
            updatedAt: nowIsoString,
          })
        }

        const hasDraftIdAfterUpdate = nextDraftItems.some((draftItem) => draftItem.id === draftId)
        const nextDraftStore: WriteRegistrationDraftStore = {
          version: 2,
          activeDraftId: hasDraftIdAfterUpdate ? draftId : (nextDraftItems[0]?.id || null),
          drafts: nextDraftItems,
        }

        const nextBigStringValue = serializeWriteRegistrationDraftStore(nextDraftStore)
        const currentBigStringValue = fieldInTicket.bigStringValue || undefined

        if (currentBigStringValue === nextBigStringValue) {
          console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-no-change', {
            pipelineFieldId: Number(field.pipelineFieldId),
            draftId,
            writeRegistrationType,
          })
          return fieldInTicket
        }

        hasChanges = true
        console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-field-updated', {
          pipelineFieldId: Number(field.pipelineFieldId),
          draftId,
          writeRegistrationType,
          previousDraftCount: currentDraftStore.drafts.length,
          nextDraftCount: nextDraftStore.drafts.length,
          nextActiveDraftId: nextDraftStore.activeDraftId,
          nextBigStringLength: typeof nextBigStringValue === 'string' ? nextBigStringValue.length : 0,
        })
        return {
          ...fieldInTicket,
          bigStringValue: nextBigStringValue,
        }
      })

      if (!hasChanges) {
        console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-skip-setvalue', {
          pipelineFieldId: Number(field.pipelineFieldId),
          draftId,
          writeRegistrationType,
        })
        return prev
      }

      console.debug('[PIPELINE-WRITE-STAGED][FRONT] persist-stage-draft-setvalue', {
        pipelineFieldId: Number(field.pipelineFieldId),
        draftId,
        writeRegistrationType,
        totalFieldCount: nextFieldInTickets.length,
      })
      return {
        ...prev,
        fieldInTickets: nextFieldInTickets,
      }
    })
  }, [activeWriteRegistrationDraftId, createWriteRegistrationDraftId, field.pipelineFieldId, normalizeWriteRegistrationDraftData, parseWriteRegistrationDraftStore, serializeWriteRegistrationDraftStore, setValue, stagedWriteRegistrationEnabled, writeRegistrationType])

  const selectActiveWriteRegistrationDraft = React.useCallback((draftId: string) => {
    if (!stagedWriteRegistrationEnabled) return
    if (!draftId) return
    const nowIsoString = new Date().toISOString()

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] select-active-draft-request', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      draftId,
      currentActiveDraftId: activeWriteRegistrationDraftId,
    })

    setValue((prev) => {
      let hasChanges = false

      const nextFieldInTickets = prev.fieldInTickets.map((fieldInTicket) => {
        if (!isSameWriteRegistrationGroup(fieldInTicket)) return fieldInTicket

        const currentDraftStore = parseWriteRegistrationDraftStore(fieldInTicket.bigStringValue)
        const hasDraft = currentDraftStore.drafts.some((draftItem) => draftItem.id === draftId)
        const nextDraftItems = hasDraft
          ? currentDraftStore.drafts
          : [...currentDraftStore.drafts, { id: draftId, data: {}, createdAt: nowIsoString, updatedAt: nowIsoString }]

        const nextDraftStore: WriteRegistrationDraftStore = {
          version: 2,
          activeDraftId: draftId,
          drafts: nextDraftItems,
        }

        const nextBigStringValue = serializeWriteRegistrationDraftStore(nextDraftStore)
        if ((fieldInTicket.bigStringValue || undefined) === nextBigStringValue) {
          console.debug('[PIPELINE-WRITE-STAGED][FRONT] select-active-draft-no-change-field', {
            sourcePipelineFieldId: Number(field.pipelineFieldId),
            targetPipelineFieldId: Number(fieldInTicket.pipelineFieldId),
            writeRegistrationType,
            draftId,
            currentActiveDraftId: currentDraftStore.activeDraftId,
            hasDraft,
          })
          return fieldInTicket
        }

        hasChanges = true
        console.debug('[PIPELINE-WRITE-STAGED][FRONT] select-active-draft-field-updated', {
          sourcePipelineFieldId: Number(field.pipelineFieldId),
          targetPipelineFieldId: Number(fieldInTicket.pipelineFieldId),
          writeRegistrationType,
          draftId,
          previousActiveDraftId: currentDraftStore.activeDraftId,
          nextActiveDraftId: nextDraftStore.activeDraftId,
          previousDraftIds: currentDraftStore.drafts.map((draftItem) => draftItem.id),
          nextDraftIds: nextDraftStore.drafts.map((draftItem) => draftItem.id),
          hasDraftBeforeUpdate: hasDraft,
        })

        return {
          ...fieldInTicket,
          bigStringValue: nextBigStringValue,
        }
      })

      if (!hasChanges) {
        console.debug('[PIPELINE-WRITE-STAGED][FRONT] select-active-draft-skip-setvalue', {
          pipelineFieldId: Number(field.pipelineFieldId),
          writeRegistrationType,
          draftId,
        })
        return prev
      }

      console.debug('[PIPELINE-WRITE-STAGED][FRONT] select-active-draft-setvalue', {
        pipelineFieldId: Number(field.pipelineFieldId),
        writeRegistrationType,
        draftId,
      })

      return {
        ...prev,
        fieldInTickets: nextFieldInTickets,
      }
    })
  }, [activeWriteRegistrationDraftId, field.pipelineFieldId, isSameWriteRegistrationGroup, parseWriteRegistrationDraftStore, serializeWriteRegistrationDraftStore, setValue, stagedWriteRegistrationEnabled, writeRegistrationType])

  const handleSelectWriteRegistrationDraft = React.useCallback((draftId: string) => {
    console.debug('[PIPELINE-WRITE-STAGED][FRONT] modal-select-draft-callback', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      draftId,
      activeWriteRegistrationDraftId,
    })
    setWriteRegistrationAutoCreateFeedback(null)
    selectActiveWriteRegistrationDraft(draftId)
  }, [activeWriteRegistrationDraftId, field.pipelineFieldId, selectActiveWriteRegistrationDraft, writeRegistrationType])

  const createNewWriteRegistrationDraft = React.useCallback(() => {
    if (!stagedWriteRegistrationEnabled) return

    const nextDraftId = createWriteRegistrationDraftId()
    const nowIsoString = new Date().toISOString()

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] create-new-draft-request', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      nextDraftId,
      currentActiveDraftId: activeWriteRegistrationDraftId,
      draftOptionsCount: writeRegistrationDraftOptions.length,
    })

    setValue((prev) => ({
      ...prev,
      fieldInTickets: prev.fieldInTickets.map((fieldInTicket) => {
        if (!isSameWriteRegistrationGroup(fieldInTicket)) return fieldInTicket

        const currentDraftStore = parseWriteRegistrationDraftStore(fieldInTicket.bigStringValue)
        const alreadyExists = currentDraftStore.drafts.some((draftItem) => draftItem.id === nextDraftId)

        const nextDraftItems = alreadyExists
          ? currentDraftStore.drafts
          : [...currentDraftStore.drafts, { id: nextDraftId, data: {}, createdAt: nowIsoString, updatedAt: nowIsoString }]

        const nextDraftStore: WriteRegistrationDraftStore = {
          version: 2,
          activeDraftId: nextDraftId,
          drafts: nextDraftItems,
        }

        return {
          ...fieldInTicket,
          bigStringValue: serializeWriteRegistrationDraftStore(nextDraftStore),
        }
      }),
    }))
  }, [activeWriteRegistrationDraftId, createWriteRegistrationDraftId, field.pipelineFieldId, isSameWriteRegistrationGroup, parseWriteRegistrationDraftStore, serializeWriteRegistrationDraftStore, setValue, stagedWriteRegistrationEnabled, writeRegistrationDraftOptions.length, writeRegistrationType])

  const handleCreateWriteRegistrationDraft = React.useCallback(() => {
    console.debug('[PIPELINE-WRITE-STAGED][FRONT] modal-create-draft-callback', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      activeWriteRegistrationDraftId,
      draftOptionsCount: writeRegistrationDraftOptions.length,
    })
    setWriteRegistrationAutoCreateFeedback(null)
    createNewWriteRegistrationDraft()
  }, [activeWriteRegistrationDraftId, createNewWriteRegistrationDraft, field.pipelineFieldId, writeRegistrationDraftOptions.length, writeRegistrationType])

  const handleStageDraftChangeFromModal = React.useCallback((draft: Record<string, any>) => {
    console.debug('[PIPELINE-WRITE-STAGED][FRONT] modal-stage-draft-change-callback', {
      pipelineFieldId: Number(field.pipelineFieldId),
      writeRegistrationType,
      activeWriteRegistrationDraftId,
      draftKeys: Object.keys(draft || {}),
    })
    persistWriteRegistrationStageDraft(draft)
  }, [activeWriteRegistrationDraftId, field.pipelineFieldId, persistWriteRegistrationStageDraft, writeRegistrationType])

  const fullRequiredRegistrationFieldKeys = React.useMemo(() => {
    if (!writeRegistrationType) return requiredRegistrationFieldKeys
    return writeRegistrationDefaultRequiredKeys[writeRegistrationType] || requiredRegistrationFieldKeys
  }, [requiredRegistrationFieldKeys, writeRegistrationDefaultRequiredKeys, writeRegistrationType])

  const hasAggregatedDraftValue = React.useCallback((key: string): boolean => {
    const value = aggregatedWriteRegistrationDraft[key]

    if (key === 'material') {
      const hasMaterialName = typeof aggregatedWriteRegistrationDraft.materialName === 'string' && aggregatedWriteRegistrationDraft.materialName.trim() !== ''
      const hasMaterialId = Number.isFinite(Number(aggregatedWriteRegistrationDraft.materialId))
      const hasMaterialValue = typeof value === 'string' && value.trim() !== ''
      return hasMaterialName || hasMaterialId || hasMaterialValue
    }

    if (key === 'materials') {
      if (writeRegistrationType === 'collection') {
        const hasMaterialIds = Array.isArray(aggregatedWriteRegistrationDraft.materialIds) && aggregatedWriteRegistrationDraft.materialIds.length > 0
        const hasAviamentoSelections = Array.isArray(aggregatedWriteRegistrationDraft.collectionAviamentoSelections)
          && aggregatedWriteRegistrationDraft.collectionAviamentoSelections.length > 0
        return hasMaterialIds && hasAviamentoSelections
      }

      if (Array.isArray(value)) return value.length > 0
      return false
    }

    if (key === 'pantoneCode') {
      return typeof aggregatedWriteRegistrationDraft.pantoneCode === 'string' && aggregatedWriteRegistrationDraft.pantoneCode.trim() !== ''
    }

    if (key === 'colors') {
      const colorsValue = aggregatedWriteRegistrationDraft.colors
      return Array.isArray(colorsValue) && colorsValue.length > 0
    }

    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'string') return value.trim() !== ''
    if (typeof value === 'number') return Number.isFinite(value)
    if (typeof value === 'boolean') return true
    if (value && typeof value === 'object') return Object.keys(value).length > 0
    return false
  }, [aggregatedWriteRegistrationDraft, writeRegistrationType])

  const missingRequiredRegistrationFieldKeys = React.useMemo(() => {
    return fullRequiredRegistrationFieldKeys.filter((key) => !hasAggregatedDraftValue(key))
  }, [fullRequiredRegistrationFieldKeys, hasAggregatedDraftValue])

  const canAutoCreateWriteRegistration = React.useMemo(() => {
    if (!stagedWriteRegistrationEnabled) return false
    if (!writeRegistrationType) return false
    if (!activeWriteRegistrationDraftId) return false
    if (fullRequiredRegistrationFieldKeys.length === 0) return false
    return missingRequiredRegistrationFieldKeys.length === 0
  }, [activeWriteRegistrationDraftId, fullRequiredRegistrationFieldKeys.length, missingRequiredRegistrationFieldKeys.length, stagedWriteRegistrationEnabled, writeRegistrationType])

  useEffect(() => {
    if (!stagedWriteRegistrationEnabled || !writeRegistrationType || !activeWriteRegistrationDraftId) return

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-evaluation', {
      writeRegistrationType,
      activeWriteRegistrationDraftId,
      requiredKeys: fullRequiredRegistrationFieldKeys,
      missingRequiredKeys: missingRequiredRegistrationFieldKeys,
      canAutoCreateWriteRegistration,
      aggregatedDraftKeys: Object.keys(aggregatedWriteRegistrationDraft || {}),
    })
  }, [
    activeWriteRegistrationDraftId,
    aggregatedWriteRegistrationDraft,
    canAutoCreateWriteRegistration,
    fullRequiredRegistrationFieldKeys,
    missingRequiredRegistrationFieldKeys,
    stagedWriteRegistrationEnabled,
    writeRegistrationType,
  ])

  const toNumericIdArray = React.useCallback((value: any): number[] => {
    if (!Array.isArray(value)) return []
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item))
  }, [])

  const toObjectArray = React.useCallback((value: any): any[] => {
    if (!Array.isArray(value)) return []
    return value.filter((item) => item && typeof item === 'object')
  }, [])

  const buildWriteRegistrationViewUrl = React.useCallback((type: IntegrationConfig['writeRegistrationType'], createdId: number | null): string | null => {
    if (type === 'collection') {
      if (Number.isFinite(createdId)) return `/panel/collection/edit/${Number(createdId)}`
      return '/panel/collection'
    }

    if (type === 'family') return '/panel/families'
    if (type === 'product') return '/panel/collection'
    if (type === 'fabric') return '/panel/stock'

    if (type === 'trim') {
      if (Number.isFinite(createdId)) return `/panel/stock/resource/view?id=${Number(createdId)}`
      return '/panel/stock/resource'
    }

    return null
  }, [])

  const buildAutoCreateExecutionKey = React.useCallback((draftId: string): string => {
    const ticketId = Number.isFinite(Number(field.ticketId)) ? String(field.ticketId) : 'no-ticket'
    const registrationType = String(writeRegistrationType || 'unknown')
    return `${ticketId}:${registrationType}:${draftId}`
  }, [field.ticketId, writeRegistrationType])

  const readPersistedAutoCreateStatus = React.useCallback((executionKey: string): 'done' | 'in-flight' | null => {
    if (typeof window === 'undefined') return null

    const storageKey = `pipeline-write-autocreate:${executionKey}`
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) return null

    try {
      const parsed = JSON.parse(raw)
      const status = String(parsed?.status || '').trim()
      const timestamp = Number(parsed?.timestamp)

      if (status === 'in-flight' && Number.isFinite(timestamp) && (Date.now() - timestamp > 120000)) {
        window.sessionStorage.removeItem(storageKey)
        return null
      }

      if (status === 'done' || status === 'in-flight') return status
      return null
    } catch {
      window.sessionStorage.removeItem(storageKey)
      return null
    }
  }, [])

  const writePersistedAutoCreateStatus = React.useCallback((executionKey: string, status: 'done' | 'in-flight') => {
    if (typeof window === 'undefined') return
    const storageKey = `pipeline-write-autocreate:${executionKey}`
    window.sessionStorage.setItem(storageKey, JSON.stringify({ status, timestamp: Date.now() }))
  }, [])

  const clearPersistedAutoCreateStatus = React.useCallback((executionKey: string) => {
    if (typeof window === 'undefined') return
    const storageKey = `pipeline-write-autocreate:${executionKey}`
    window.sessionStorage.removeItem(storageKey)
  }, [])

  useEffect(() => {
    if (!canCreateRecordFromWriteIntegration) return
    if (!writeRegistrationCreateOnSaveToken || writeRegistrationCreateOnSaveToken <= 0) return
    if (writeRegistrationCreateOnSaveToken <= lastHandledWriteRegistrationCreateOnSaveTokenRef.current) return

    lastHandledWriteRegistrationCreateOnSaveTokenRef.current = writeRegistrationCreateOnSaveToken

    if (!canAutoCreateWriteRegistration) {
      console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-skip-not-ready-on-save', {
        writeRegistrationType,
        activeWriteRegistrationDraftId,
        writeRegistrationCreateOnSaveToken,
        missingRequiredRegistrationFieldKeys,
      })
      return
    }

    if (isAutoCreatingWriteRegistration) return
    if (!activeWriteRegistrationDraftId) return

    const executionKey = buildAutoCreateExecutionKey(activeWriteRegistrationDraftId)
    const persistedStatus = readPersistedAutoCreateStatus(executionKey)
    const isInFlightRuntime = autoCreateInFlightKeysRef.current.has(executionKey)
    const isCompletedRuntime = autoCreateCompletedKeysRef.current.has(executionKey)

    console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-trigger-check', {
      executionKey,
      writeRegistrationType,
      activeWriteRegistrationDraftId,
      canAutoCreateWriteRegistration,
      isAutoCreatingWriteRegistration,
      writeRegistrationCreateOnSaveToken,
      isInFlightRuntime,
      isCompletedRuntime,
      persistedStatus,
      missingRequiredRegistrationFieldKeys,
    })

    if (isCompletedRuntime || persistedStatus === 'done') {
      console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-skip-already-completed', {
        executionKey,
        writeRegistrationType,
        activeWriteRegistrationDraftId,
      })
      return
    }

    if (isInFlightRuntime || persistedStatus === 'in-flight') {
      console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-skip-in-flight', {
        executionKey,
        writeRegistrationType,
        activeWriteRegistrationDraftId,
      })
      return
    }

    onWriteRegistrationAutoCreateActivity?.({
      phase: 'start',
      token: writeRegistrationCreateOnSaveToken,
      executionKey,
    })

    autoCreateInFlightKeysRef.current.add(executionKey)
    writePersistedAutoCreateStatus(executionKey, 'in-flight')
    setIsAutoCreatingWriteRegistration(true)

    let cancelled = false

    const createWriteRecord = async () => {
      try {
        if (!writeRegistrationType) return

        let createEndpoint = ''
        let payload: Record<string, any> = {}

        if (writeRegistrationType === 'collection') {
          createEndpoint = '/backend-fashion-orders/fashion-product/collections'

          const selectedMaterials = toNumericIdArray(aggregatedWriteRegistrationDraft.materialIds)
          const selectedAviamentos = toObjectArray(aggregatedWriteRegistrationDraft.collectionAviamentoSelections)

          const basePayload = {
            name: String(aggregatedWriteRegistrationDraft.name || '').trim(),
            gradeIds: toNumericIdArray(aggregatedWriteRegistrationDraft.gradeIds),
            fabrics: selectedMaterials.map((materialId) => ({ materialId })),
            trims: selectedAviamentos
              .map((selection) => ({ resourceId: Number(selection?.resourceId) }))
              .filter((row) => Number.isFinite(row.resourceId)),
            collectionColors: toObjectArray(aggregatedWriteRegistrationDraft.collectionColors).map((color) => ({
              name: String(color?.name || '').trim(),
              pantone: {
                code: String(color?.pantone?.code || '').trim(),
                name: color?.pantone?.name,
              },
            })).filter((color) => color.name && color.pantone.code),
          }

          const { materials: _legacyMaterials, ...draftWithoutLegacyMaterials } = aggregatedWriteRegistrationDraft as Record<string, unknown>

          payload = {
            ...draftWithoutLegacyMaterials,
            ...basePayload,
          }
        }

        if (writeRegistrationType === 'family') {
          createEndpoint = '/backend-fashion-orders/fashion-product/families'

          const basePayload = {
            name: String(aggregatedWriteRegistrationDraft.name || '').trim(),
            collectionId: Number(aggregatedWriteRegistrationDraft.collectionId),
            gradeIds: toNumericIdArray(aggregatedWriteRegistrationDraft.gradeIds),
            collectionColorIds: toNumericIdArray(aggregatedWriteRegistrationDraft.collectionColorIds),
            materials: toNumericIdArray(aggregatedWriteRegistrationDraft.materials).map((collectionMaterialId) => ({ collectionMaterialId })),
          }

          payload = {
            ...aggregatedWriteRegistrationDraft,
            ...basePayload,
          }
        }

        if (writeRegistrationType === 'product') {
          createEndpoint = '/backend-fashion-orders/fashion-product'

          const compositionItems = toObjectArray(aggregatedWriteRegistrationDraft.compositionItems)
          const productItemColors = toObjectArray(aggregatedWriteRegistrationDraft.productItemColors)

          const basePayload = {
            productCode: String(aggregatedWriteRegistrationDraft.productCode || '').trim(),
            name: String(aggregatedWriteRegistrationDraft.name || '').trim(),
            collectionId: Number(aggregatedWriteRegistrationDraft.collectionId),
            familyId: Number(aggregatedWriteRegistrationDraft.familyId),
            groupId: Number(aggregatedWriteRegistrationDraft.groupId),
            gradeIds: toNumericIdArray(aggregatedWriteRegistrationDraft.gradeIds),
            compositionItems: compositionItems
              .map((item) => ({
                tipo: 'materia-prima',
                materialId: Number(item?.materialId),
                usage: Number(item?.usage),
                unitOfMeasurementId: Number(item?.unitOfMeasurementId),
              }))
              .filter((item) => Number.isFinite(item.materialId) && Number.isFinite(item.usage) && item.usage > 0 && Number.isFinite(item.unitOfMeasurementId)),
            productItemColors: productItemColors
              .map((item) => ({
                collectionColorId: Number(item?.collectionColorId),
                materialId: Number(item?.materialId),
              }))
              .filter((item) => Number.isFinite(item.collectionColorId) && Number.isFinite(item.materialId)),
          }

          payload = {
            ...aggregatedWriteRegistrationDraft,
            ...basePayload,
          }
        }

        if (writeRegistrationType === 'fabric') {
          createEndpoint = '/backend/stock-fashion'

          const payloadMeasurementUnit = String(aggregatedWriteRegistrationDraft.measurementUnit || 'KG').toUpperCase()
          const normalizedMeasurementUnit = payloadMeasurementUnit === 'MM' || payloadMeasurementUnit === 'UN' ? payloadMeasurementUnit : 'KG'

          const quantityValue = Number(aggregatedWriteRegistrationDraft.quantity)
          const totalWeightValue = Number(aggregatedWriteRegistrationDraft.totalWeight)
          const lengthInMmValue = Number(aggregatedWriteRegistrationDraft.lengthInMm)

          const normalizedQuantity = normalizedMeasurementUnit === 'UN' && Number.isFinite(quantityValue) ? quantityValue : undefined
          const normalizedTotalWeight = normalizedMeasurementUnit === 'KG' && Number.isFinite(totalWeightValue) ? totalWeightValue : 0
          const normalizedLengthInMm = normalizedMeasurementUnit === 'MM' && Number.isFinite(lengthInMmValue) ? lengthInMmValue : undefined

          const parsedItemColorId = Number(aggregatedWriteRegistrationDraft.itemColorId)
          const itemColorId = Number.isFinite(parsedItemColorId) ? parsedItemColorId : undefined

          const {
            measurementUnit: _ignoredMeasurementUnit,
            quantity: _ignoredQuantity,
            totalWeight: _ignoredTotalWeight,
            lengthInMm: _ignoredLengthInMm,
            materialId: _ignoredMaterialId,
            materialName: _ignoredMaterialName,
            pantoneHex: _ignoredPantoneHex,
            ...fabricDraftWithoutInternal
          } = aggregatedWriteRegistrationDraft

          payload = {
            code: String(aggregatedWriteRegistrationDraft.code || '').trim(),
            material: String(aggregatedWriteRegistrationDraft.materialName || aggregatedWriteRegistrationDraft.material || '').trim(),
            measurementUnit: normalizedMeasurementUnit,
            quantity: normalizedQuantity,
            totalWeight: normalizedTotalWeight,
            lengthInMm: normalizedLengthInMm,
            itemColorId,
            color: String(aggregatedWriteRegistrationDraft.color || '').trim(),
            pantoneCode: String(aggregatedWriteRegistrationDraft.pantoneCode || '').trim(),
            pantoneName: aggregatedWriteRegistrationDraft.pantoneName,
            ...fabricDraftWithoutInternal,
          }
        }

        if (writeRegistrationType === 'trim') {
          createEndpoint = '/resources/resource'

          const payloadStockUnit = String(aggregatedWriteRegistrationDraft.stockUnit || 'UN').toUpperCase()
          const normalizedStockUnit = payloadStockUnit === 'KG' || payloadStockUnit === 'MM' ? payloadStockUnit : 'UN'

          const unitQuantityValue = Number(aggregatedWriteRegistrationDraft.unitQuantity)
          const totalWeightValue = Number(aggregatedWriteRegistrationDraft.totalWeight)
          const lengthInMmValue = Number(aggregatedWriteRegistrationDraft.lengthInMm)

          const normalizedUnitQuantity = normalizedStockUnit === 'UN' && Number.isFinite(unitQuantityValue) ? unitQuantityValue : 0
          const normalizedTotalWeight = normalizedStockUnit === 'KG' && Number.isFinite(totalWeightValue) ? totalWeightValue : undefined
          const normalizedLengthInMm = normalizedStockUnit === 'MM' && Number.isFinite(lengthInMmValue) ? lengthInMmValue : undefined

          const colorsPayload = toObjectArray(aggregatedWriteRegistrationDraft.colors)
            .map((color) => ({
              colorName: String(color?.colorName || '').trim(),
              pantone: {
                code: String(color?.pantone?.code || '').trim(),
                name: color?.pantone?.name,
              },
            }))
            .filter((color) => color.colorName && color.pantone.code)

          payload = {
            ...aggregatedWriteRegistrationDraft,
            name: String(aggregatedWriteRegistrationDraft.name || '').trim(),
            categoryId: Number(aggregatedWriteRegistrationDraft.categoryId),
            materialId: Number(aggregatedWriteRegistrationDraft.materialId),
            stockUnit: normalizedStockUnit,
            unitQuantity: normalizedUnitQuantity,
            totalWeight: normalizedTotalWeight,
            lengthInMm: normalizedLengthInMm,
            colors: colorsPayload,
          }
        }

        if (!createEndpoint) return

        console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-request-start', {
          executionKey,
          writeRegistrationType,
          endpoint: createEndpoint,
          payloadKeys: Object.keys(payload || {}),
        })

        const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}${createEndpoint}`, payload)
        const created = response.data?.data || response.data

        const createdId = Number(created?.id || created?.codeId)
        const resolvedCreatedId = Number.isFinite(createdId)
          ? createdId
          : String(created?.id || created?.codeId || '').trim() || String(payload.id || payload.code || '')
        const createdNameByModule = (() => {
          if (writeRegistrationType === 'fabric') {
            const createdDescription = String(created?.description || created?.material || payload.code || '').trim()
            const createdColor = String(
              created?.color ||
              created?.itemColor?.name ||
              created?.ItemColor?.name ||
              payload.color ||
              ''
            ).trim()
            const createdPantoneCode = String(
              created?.pantoneCode ||
              created?.itemColor?.Pantone?.code ||
              created?.ItemColor?.Pantone?.code ||
              payload.pantoneCode ||
              ''
            ).trim()
            const createdDisplayName = [createdDescription, createdColor].filter(Boolean).join(' - ')
            return [createdDisplayName, createdPantoneCode ? `(${createdPantoneCode})` : ''].filter(Boolean).join(' ') || createdDescription
          }

          if (writeRegistrationType === 'product') {
            return String(created?.name || payload.name || payload.productCode || '').trim()
          }

          return String(created?.name || payload.name || payload.code || '').trim()
        })()

        const createdOption = {
          id: Number.isFinite(createdId) ? createdId : resolvedCreatedId,
          name: createdNameByModule || String(payload.name || payload.code || createdId),
          _displayName: createdNameByModule || String(payload.name || payload.code || createdId),
          color: String(created?.color || payload.color || '').trim(),
          pantoneCode: String(created?.pantoneCode || payload.pantoneCode || '').trim(),
          _swatchHex:
            created?.pantoneHex ||
            created?.itemColor?.Pantone?.hex ||
            created?.ItemColor?.Pantone?.hex ||
            aggregatedWriteRegistrationDraft.pantoneHex,
          ...created,
        }

        autoCreateCompletedKeysRef.current.add(executionKey)
        writePersistedAutoCreateStatus(executionKey, 'done')

        console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-request-success', {
          executionKey,
          writeRegistrationType,
          createdId: resolvedCreatedId,
          createdName: createdNameByModule,
          wasCancelledDuringRequest: cancelled,
        })

        if (cancelled) {
          console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-success-after-cancelled-cycle', {
            executionKey,
            writeRegistrationType,
            createdId: resolvedCreatedId,
          })
        }

        setWriteRegistrationAutoCreateFeedback({
          id: resolvedCreatedId,
          name: createdNameByModule || String(payload.name || payload.code || resolvedCreatedId),
          viewUrl: buildWriteRegistrationViewUrl(writeRegistrationType, Number.isFinite(createdId) ? createdId : null),
        })

        setValue((prev) => ({
          ...prev,
          fieldInTickets: prev.fieldInTickets.map((fieldInTicket) => {
            if (!isSameWriteRegistrationGroup(fieldInTicket)) return fieldInTicket

            const currentSelections = Array.isArray((fieldInTicket as any).integrationDataSelections)
              ? [...(fieldInTicket as any).integrationDataSelections]
              : []

            if ((fieldInTicket as any).integrationData) {
              currentSelections.push((fieldInTicket as any).integrationData)
            }

            const dedupeByKey = new Set<string>()
            const nextSelections = [...currentSelections, createdOption].filter((selectionOption) => {
              const selectionId = selectionOption?.id ?? selectionOption?.integerValue ?? selectionOption?.name
              const selectionName = String(selectionOption?.name || '').trim().toLowerCase()
              const dedupeKey = `${String(selectionId)}::${selectionName}`
              if (dedupeByKey.has(dedupeKey)) return false
              dedupeByKey.add(dedupeKey)
              return true
            })

            const firstSelectionWithNumericId = nextSelections.find((selectionOption) => Number.isFinite(Number(selectionOption?.id)))
            const nextIntegerValue = firstSelectionWithNumericId
              ? Number(firstSelectionWithNumericId.id)
              : fieldInTicket.integerValue

            const currentDraftStore = parseWriteRegistrationDraftStore(fieldInTicket.bigStringValue)
            const nextDraftItems = currentDraftStore.drafts.filter((draftItem) => draftItem.id !== activeWriteRegistrationDraftId)
            const nextDraftStore: WriteRegistrationDraftStore = {
              version: 2,
              activeDraftId: nextDraftItems[0]?.id || null,
              drafts: nextDraftItems,
            }

            return {
              ...fieldInTicket,
              integerValue: nextIntegerValue,
              stringValue: nextSelections.map((selectionOption) => String(selectionOption?.name || '')).filter((selectionName) => selectionName.trim() !== '').join(', '),
              bigStringValue: serializeWriteRegistrationDraftStore(nextDraftStore),
              integrationData: nextSelections[0] || createdOption,
              integrationDataSelections: nextSelections,
            }
          }),
        }))
      } catch (error) {
        autoCreateCompletedKeysRef.current.delete(executionKey)
        clearPersistedAutoCreateStatus(executionKey)

        if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED') {
          console.debug('[PIPELINE-WRITE-STAGED][FRONT] auto-create-request-cancelled', {
            executionKey,
            writeRegistrationType,
          })
          return
        }

        console.error('[PIPELINE-WRITE-STAGED] auto-create-request-error-context', {
          executionKey,
          writeRegistrationType,
          activeWriteRegistrationDraftId,
          payloadDraftKeys: Object.keys(aggregatedWriteRegistrationDraft || {}),
        })
        console.error('[PIPELINE-WRITE-STAGED] erro ao criar registro automático:', error)
      } finally {
        autoCreateInFlightKeysRef.current.delete(executionKey)
        onWriteRegistrationAutoCreateActivity?.({
          phase: 'finish',
          token: writeRegistrationCreateOnSaveToken,
          executionKey,
        })
        if (!cancelled) {
          setIsAutoCreatingWriteRegistration(false)
        }
      }
    }

    createWriteRecord()

    return () => {
      cancelled = true
    }
  }, [activeWriteRegistrationDraftId, aggregatedWriteRegistrationDraft, buildAutoCreateExecutionKey, buildWriteRegistrationViewUrl, canAutoCreateWriteRegistration, canCreateRecordFromWriteIntegration, clearPersistedAutoCreateStatus, isAutoCreatingWriteRegistration, isSameWriteRegistrationGroup, missingRequiredRegistrationFieldKeys, onWriteRegistrationAutoCreateActivity, parseWriteRegistrationDraftStore, readPersistedAutoCreateStatus, serializeWriteRegistrationDraftStore, setValue, toNumericIdArray, toObjectArray, writePersistedAutoCreateStatus, writeRegistrationCreateOnSaveToken, writeRegistrationType])

  const fetchIntegrationData = React.useCallback(async (limit: number, offset: number, search?: string) => {
    if (!integrationConfig) {
      return []
    }
    
    const moduleConfig = getModuleById(integrationConfig.moduleId)
    const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig.fieldKey)
    const integrationEndpoint = String(integrationConfig.endpoint || '').toLowerCase()
    const integrationModuleName = String(integrationConfig.moduleName || '').toLowerCase()
    const isLegacyFabricIntegration =
      integrationEndpoint.includes('/backend/material/list/stock') ||
      integrationModuleName.includes('tecido')
    const isFashionFabricWriteModule =
      integrationConfig.integrationType === 'write' &&
      (
        integrationConfig.moduleId === 'fashion-fabrics-write' ||
        integrationConfig.moduleId === 'fashion-stock' ||
        integrationConfig.writeRegistrationType === 'fabric' ||
        integrationModuleName.includes('tecido') ||
        integrationEndpoint.includes('/stock')
      )
    const isFashionFabricIntegration = isFashionFabricWriteModule || isLegacyFabricIntegration
    const resolvedEndpoint = isFashionFabricIntegration
      ? '/backend/stock/integration'
      : (moduleConfig?.endpoint || integrationConfig.endpoint)
    const url = `${process.env.REACT_APP_GATEWAY_BASE_URL}${resolvedEndpoint}`
    const paginationType = isFashionFabricIntegration
      ? 'skip-take'
      : (moduleConfig?.paginationType || (integrationConfig as any).paginationType || 'offset')
    const responseKey = isFashionFabricIntegration
      ? 'items'
      : (moduleConfig?.responseKey ?? (integrationConfig as any).responseKey)

    const debugPrefix = '[fetchIntegrationData][debug]'
    const fabricDebugPrefix = '[fetchIntegrationData][fabric-debug]'
    console.log(`${debugPrefix} request-start`, {
      moduleId: integrationConfig.moduleId,
      fieldKey,
      booleanFilterValue: integrationConfig.booleanFilterValue,
      url,
      paginationType,
      responseKey,
      limit,
      offset,
      search,
    })

    const shouldExpandInitialWindow =
      (!search || !search.trim()) &&
      (
        integrationConfig.moduleId === 'companies' ||
        integrationConfig.moduleId === 'resources' ||
        integrationConfig.moduleId === 'fashion-stock' ||
        integrationConfig.moduleId === 'steel-stock'
      )

    const isStockModule = integrationConfig.moduleId === 'fashion-stock' || integrationConfig.moduleId === 'steel-stock' || isFashionFabricIntegration
    const isResourcesModule = integrationConfig.moduleId === 'resources'
    const selectedFieldConfig = moduleConfig?.fields?.find((field) => field.field === fieldKey)
    const shouldPreferLocalSearch = !!selectedFieldConfig?.filterEmpty || isResourcesModule

    const effectiveLimit = shouldExpandInitialWindow
      ? Math.max(limit, 100)
      : limit

    const hasSearchTerm = !!(search && search.trim())
    const hasBooleanFilterConfigured = typeof integrationConfig.booleanFilterValue === 'boolean'
    const stockBooleanCompatibleField = fieldKey === 'isActivated' || fieldKey === 'status'
    const shouldSendBackendIsActivatedFilter = hasBooleanFilterConfigured && isStockModule && stockBooleanCompatibleField
    const shouldUseBackendSearch = hasSearchTerm && !isStockModule && !shouldPreferLocalSearch
    const localSearchWindowLimit = hasSearchTerm && !shouldUseBackendSearch
      ? Math.max(limit, isStockModule || isResourcesModule ? 500 : 200)
      : effectiveLimit
    const localSearchOffset = hasSearchTerm && !shouldUseBackendSearch ? 0 : offset
    const requestLimit = hasSearchTerm
      ? Math.max(limit, isStockModule ? 500 : 100)
      : effectiveLimit

    let params: any
    if (hasSearchTerm && shouldUseBackendSearch) {
      params = paginationType === 'page'
        ? { limit: requestLimit, page: Math.floor(offset / requestLimit) + 1, search }
        : paginationType === 'skip-take'
        ? { skip: offset, take: requestLimit, search }
        : { limit: requestLimit, offset, search }
    } else {
      params = paginationType === 'page'
        ? { limit: localSearchWindowLimit, page: Math.floor(localSearchOffset / localSearchWindowLimit) + 1 }
        : paginationType === 'skip-take'
        ? { skip: localSearchOffset, take: localSearchWindowLimit }
        : { limit: localSearchWindowLimit, offset: localSearchOffset }
    }

    if (shouldSendBackendIsActivatedFilter) {
      params = {
        ...params,
        isActivated: integrationConfig.booleanFilterValue,
      }
    }

    console.log(`${debugPrefix} request-params`, {
      hasSearchTerm,
      shouldUseBackendSearch,
      isStockModule,
      shouldPreferLocalSearch,
      shouldExpandInitialWindow,
      effectiveLimit,
      requestLimit,
      localSearchWindowLimit,
      localSearchOffset,
      shouldSendBackendIsActivatedFilter,
      params,
    })
    
    try {
      const response = await axios.get(url, { params })
      const raw = response.data
      const extracted = (() => {
        if (responseKey) {
          const keyed = raw?.[responseKey]
          if (Array.isArray(keyed)) return keyed
        }
        if (Array.isArray(raw)) return raw
        if (Array.isArray(raw?.data)) return raw.data
        if (Array.isArray(raw?.content)) return raw.content
        if (Array.isArray(raw?.items)) return raw.items
        if (Array.isArray(raw?.results)) return raw.results
        if (raw && typeof raw === 'object') {
          const firstArrayKey = Object.keys(raw).find(k => Array.isArray(raw[k]))
          if (firstArrayKey) return raw[firstArrayKey]
        }
        return raw
      })()
      const data = Array.isArray(extracted) ? extracted : []

      console.log(`${debugPrefix} response-summary`, {
        extractedIsArray: Array.isArray(extracted),
        rawKeys: raw && typeof raw === 'object' ? Object.keys(raw).slice(0, 10) : [],
        dataLength: data.length,
        firstRowId: data[0]?.id,
        firstRowFieldValue: data.length > 0 ? getNestedValue(data[0], fieldKey) : undefined,
      })

      if (isFashionFabricIntegration) {
        console.log(`${fabricDebugPrefix} response-preview`, {
          totalRows: data.length,
          preview: data.slice(0, 15).map((item: any) => ({
            id: item?.id,
            description: item?.description,
            material: item?.material,
            color: item?.color,
            itemColorName: item?.itemColor?.name,
            ItemColorName: item?.ItemColor?.name,
            pantoneCode: item?.pantoneCode,
            itemColorPantoneCode: item?.itemColor?.Pantone?.code,
            ItemColorPantoneCode: item?.ItemColor?.Pantone?.code,
          })),
        })
      }
      
      const sampleFieldValue = data.length > 0 ? getNestedValue(data[0], fieldKey) : undefined
      const isArrayField = Array.isArray(sampleFieldValue)
      const fieldConfig = moduleConfig?.fields?.find(f => f.field === fieldKey)

      let resultData: any[]
      if (isArrayField && fieldConfig?.filterEmpty) {

        const seenIds = new Set<any>()
        const expanded: any[] = []
        for (const record of data) {
          const arr = getNestedValue(record, fieldKey)
          if (!Array.isArray(arr) || arr.length === 0) continue
          for (const element of arr) {
            const elemId = element?.id
            if (elemId !== undefined && seenIds.has(elemId)) continue
            if (elemId !== undefined) seenIds.add(elemId)
            const displayName = extractDisplayName(element)
            if (!displayName) continue
            const swatchHex = getSwatchHex(element) || getSwatchHex(record)
            expanded.push({ ...element, _parentRecord: record, _displayName: displayName, _swatchHex: swatchHex })
          }
        }
        resultData = expanded
      } else if (isArrayField) {
        resultData = data
      } else {
        if (isFashionFabricIntegration) {
          const beforeFabricFilterCount = data.length
          resultData = data.filter((item: any) => {
            const hasConfiguredField = hasValidScalarValue(item, fieldKey)
            const hasColor =
              isMeaningfulValue(item?.color) ||
              isMeaningfulValue(item?.itemColor?.name) ||
              isMeaningfulValue(item?.ItemColor?.name)
            const hasMaterial = isMeaningfulValue(item?.material)
            const hasCode = isMeaningfulValue(item?.code)
            return hasConfiguredField || hasColor || hasMaterial || hasCode
          })
          console.log(`${fabricDebugPrefix} after-initial-filter`, {
            beforeCount: beforeFabricFilterCount,
            afterCount: resultData.length,
          })
        } else {
          resultData = data.filter((item: any) => hasValidScalarValue(item, fieldKey))
        }
      }

      if (typeof integrationConfig.booleanFilterValue === 'boolean') {
        const normalizeBooleanValue = (value: any): boolean | null => {
          if (typeof value === 'boolean') return value
          if (typeof value === 'number') {
            if (value === 1) return true
            if (value === 0) return false
            return null
          }
          if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase()
            if (normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'ativo' || normalizedValue === 'active') return true
            if (normalizedValue === 'false' || normalizedValue === '0' || normalizedValue === 'inativo' || normalizedValue === 'inactive') return false
            return null
          }
          return null
        }

        const targetBooleanValue = integrationConfig.booleanFilterValue
        const booleanFieldKeyForFilter = isStockModule && fieldKey === 'status' ? 'isActivated' : fieldKey
        const beforeBooleanFilterCount = resultData.length

        resultData = resultData.filter((item: any) => {
          const fieldValue = getFieldValueWithFallback(item, booleanFieldKeyForFilter)
          if (Array.isArray(fieldValue)) {
            return fieldValue.some((nestedValue) => normalizeBooleanValue(nestedValue) === targetBooleanValue)
          }
          return normalizeBooleanValue(fieldValue) === targetBooleanValue
        })

        console.log(`${debugPrefix} boolean-filter`, {
          moduleId: integrationConfig.moduleId,
          fieldKey,
          booleanFieldKeyForFilter,
          targetBooleanValue,
          beforeBooleanFilterCount,
          afterBooleanFilterCount: resultData.length,
        })
      }

      const isBooleanIntegrationField = fieldConfig?.valueType === 'boolean' || typeof integrationConfig.booleanFilterValue === 'boolean'
      const shouldDeduplicateByDisplayValue = fieldKey !== 'id' && !isBooleanIntegrationField

      if (isFashionFabricIntegration) {
        resultData = resultData.map((item: any) => {
          const description = String(item?.description || item?.material || item?.code || item?.id || '').trim()
          const resolvedColor = String(
            item?.color ||
            item?.itemColor?.name ||
            item?.ItemColor?.name ||
            item?.pantoneName ||
            item?.itemColor?.Pantone?.name ||
            item?.ItemColor?.Pantone?.name ||
            ''
          ).trim()
          const pantoneCode = String(
            item?.pantoneCode ||
            item?.itemColor?.Pantone?.code ||
            item?.ItemColor?.Pantone?.code ||
            ''
          ).trim()
          const composedName = [description, resolvedColor].filter(Boolean).join(' - ')
          const composedWithPantone = [composedName, pantoneCode ? `(${pantoneCode})` : ''].filter(Boolean).join(' ')
          const normalizedComposed = composedWithPantone || description || resolvedColor || String(item?.id || '')
          return {
            ...item,
            name: normalizedComposed,
            color: resolvedColor,
            _displayName: normalizedComposed,
            _swatchHex:
              item?._swatchHex ||
              item?.pantoneHex ||
              item?.itemColor?.Pantone?.hex ||
              item?.ItemColor?.Pantone?.hex,
          }
        })

        console.log(`${fabricDebugPrefix} after-display-map`, {
          mappedCount: resultData.length,
          preview: resultData.slice(0, 15).map((item: any) => ({
            id: item?.id,
            displayName: item?._displayName,
            color: item?.color,
            itemColorName: item?.itemColor?.name,
            ItemColorName: item?.ItemColor?.name,
            pantoneCode: item?.pantoneCode || item?.itemColor?.Pantone?.code || item?.ItemColor?.Pantone?.code,
          })),
        })
      }

      if (shouldDeduplicateByDisplayValue) {
        const deduplicatedByDisplayValue = new Map<string, any>()
        resultData.forEach((item: any) => {
          const displayValue = item?._displayName || getDisplayValueFromField(item, fieldKey) || getPrimaryLabel(item)
          const normalizedDisplayValue = String(displayValue || '').trim().toLowerCase()
          if (!normalizedDisplayValue) return
          if (!deduplicatedByDisplayValue.has(normalizedDisplayValue)) {
            const swatchHex = item?._swatchHex || getSwatchHex(item)
            deduplicatedByDisplayValue.set(normalizedDisplayValue, {
              ...item,
              _displayName: item?._displayName || String(displayValue),
              _swatchHex: swatchHex,
            })
          }
        })
        const beforeDedupCount = resultData.length
        resultData = Array.from(deduplicatedByDisplayValue.values())
        console.log(`${debugPrefix} display-dedup`, {
          moduleId: integrationConfig.moduleId,
          fieldKey,
          beforeDedupCount,
          afterDedupCount: resultData.length,
        })
      }

      resultData = resultData.map((item: any) => ({
        ...item,
        _integrationModuleId: integrationConfig.moduleId,
        _integrationFieldKey: fieldKey,
      }))

      console.log('[fetchIntegrationData] fieldKey:', fieldKey, 'total:', data.length, 'isArrayField:', isArrayField, 'filterEmpty:', fieldConfig?.filterEmpty, 'após expansão/filtro:', resultData.length)

      if (resultData.length > 0 && !sampleDataRef.current) {
        sampleDataRef.current = resultData[0]
        setSampleData(resultData[0])
      }

      if (hasSearchTerm) {
        const searchLower = search.toLowerCase().trim()
        const resolveSearchValue = (item: any): string => {
          if (isMeaningfulValue(item?._displayName)) return String(item._displayName)
          const fieldValue = getDisplayValueFromField(item, fieldKey)
          if (isMeaningfulValue(fieldValue)) return String(fieldValue)
          if (isMeaningfulValue(item?._displayName)) return String(item._displayName)
          const primaryLabel = getPrimaryLabel(item)
          if (isMeaningfulValue(primaryLabel)) return String(primaryLabel)
          return ''
        }
        const sampleMatchesPreview = resultData.slice(0, 15).map((item: any) => {
          const fieldValue = resolveSearchValue(item)
          const normalizedFieldValue = fieldValue.toLowerCase()
          const matched = isMeaningfulValue(fieldValue) && normalizedFieldValue.includes(searchLower)
          return {
            id: item?.id,
            fieldValue,
            matched,
          }
        })
        console.log(`${debugPrefix} search-preview`, {
          search,
          searchLower,
          previewSize: sampleMatchesPreview.length,
          sampleMatchesPreview,
        })

        const filteredBySearch = resultData.filter((item: any) => {
          const fieldValue = resolveSearchValue(item)
          if (!isMeaningfulValue(fieldValue)) return false
          return String(fieldValue).toLowerCase().includes(searchLower)
        })

        console.log(`${debugPrefix} search-result`, {
          search,
          beforeFilterCount: resultData.length,
          afterFilterCount: filteredBySearch.length,
          consumedCount: data.length,
          requestedCount: requestLimit,
        })

        ;(filteredBySearch as any)._consumedCount = data.length
        ;(filteredBySearch as any)._requestedCount = requestLimit
        setIntegrationEndpointHasError(false)
        return filteredBySearch
      }

      console.log(`${debugPrefix} request-result`, {
        hasSearchTerm,
        resultCount: resultData.length,
        consumedCount: data.length,
        requestedCount: requestLimit,
      })

      if (isFashionFabricIntegration) {
        console.log(`${fabricDebugPrefix} final-options-preview`, {
          resultCount: resultData.length,
          preview: resultData.slice(0, 15).map((item: any) => ({
            id: item?.id,
            displayName: item?._displayName,
          })),
        })
      }

      ;(resultData as any)._consumedCount = data.length
      ;(resultData as any)._requestedCount = requestLimit
      setIntegrationEndpointHasError(false)
      return resultData
    } catch (error) {
      console.error(`${debugPrefix} request-error`, {
        moduleId: integrationConfig.moduleId,
        fieldKey,
        limit,
        offset,
        search,
        error,
      })
      setIntegrationEndpointHasError(true)
      return []
    }
  }, [integrationConfig, getNestedValue, getDisplayValueFromField, hasValidScalarValue, getNormalizedIntegrationFieldKey, isMeaningfulValue, extractDisplayName, getPrimaryLabel, getSwatchHex])

  useEffect(() => {
    if (!isIntegrated || !isIntegratedColorField || integratedSelectedOptions.length === 0) return

    const getOptionMapKeyByName = (name: string) => `name:${normalizeColorLabel(name)}`
    const getOptionMapKeyById = (id: any) => `id:${String(id)}`

    const hasHexInCache = (option: IntegratedSelectionOption) => {
      const byName = integratedColorSwatchMap[getOptionMapKeyByName(option.name)]
      const byId = integratedColorSwatchMap[getOptionMapKeyById(option.id)]
      return Boolean(byName || byId)
    }

    const missingSwatchOptions = integratedSelectedOptions.filter((option) => {
      const inlineHex = option._swatchHex || getSwatchHex(option)
      return !inlineHex && !hasHexInCache(option)
    })

    if (missingSwatchOptions.length === 0) return

    let isMounted = true

    const loadSwatches = async () => {
      const integrationData = await fetchIntegrationData(600, 0)
      if (!Array.isArray(integrationData) || integrationData.length === 0 || !isMounted) return

      const nextMap: Record<string, string> = {}
      integrationData.forEach((item: any) => {
        const itemHex = item?._swatchHex || getSwatchHex(item)
        if (!itemHex) return

        const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig?.fieldKey || 'name')
        const itemName = item?._displayName || getDisplayValueFromField(item, fieldKey) || getPrimaryLabel(item)

        if (itemName) {
          nextMap[getOptionMapKeyByName(String(itemName))] = itemHex
        }
        if (item?.id !== null && item?.id !== undefined) {
          nextMap[getOptionMapKeyById(item.id)] = itemHex
        }
      })

      if (!isMounted || Object.keys(nextMap).length === 0) return
      setIntegratedColorSwatchMap((prev) => ({ ...prev, ...nextMap }))
    }

    loadSwatches()

    return () => {
      isMounted = false
    }
  }, [fetchIntegrationData, getDisplayValueFromField, getNormalizedIntegrationFieldKey, getPrimaryLabel, getSwatchHex, integratedColorSwatchMap, integratedSelectedOptions, integrationConfig?.fieldKey, isIntegrated, isIntegratedColorField, normalizeColorLabel])

  const translateFieldLabel = (key: string): string => {
    const translations: Record<string, string> = {
      'id': 'ID',
      'name': 'Nome',
      'email': 'E-mail',
      'password': 'Senha',
      'cnpj': 'CNPJ',
      'company': 'Empresa',
      'companyName': 'Razão Social',
      'phone': 'Telefone',
      'adminUser': 'Usuário Admin',
      'picture': 'Foto',
      'approved': 'Aprovado',
      'role': 'Papel',
      'tenantId': 'ID do Tenant',
      'createdAt': 'Criado em',
      'updatedAt': 'Atualizado em',
      'updateAt': 'Atualizado em',
      'expiresAt': 'Expira em',
      'token': 'Token',
      'resetToken': 'Token de Reset',
      'resetTokenExpiresAt': 'Token expira em',
      'code': 'Código',
      'density': 'Densidade',
      'stateRegistration': 'Inscrição Estadual',
      'contactName': 'Nome do Contato',
      'activedDays': 'Dias Ativos',
      'startTime': 'Início',
      'endTime': 'Término',
      'addressId': 'ID do Endereço',
      'cpf': 'CPF',
      'isMain': 'Principal',
      'typeName': 'Nome do Tipo',
      'status': 'Status',
      'productionPace': 'Ritmo de Produção',
      'productionDay': 'Dia de Produção',
      'codeOfOP': 'Código da OP',
      'integratedWithOmie': 'Integrado com Omie',
      'usedQuantity': 'Qtd Usada',
      'usedUnitOfMeasurement': 'Unid Medida Usada',
      'action': 'Ação',
      'dateTime': 'Data e Hora',
      'description': 'Descrição',
      'newValue': 'Novo Valor',
      'oldValue': 'Antigo Valor',
      'completed': 'Concluído',
      'observation': 'Observação',
      'index': 'Índice',
      'fileUrl': 'URL do Arquivo',
      'executeDate': 'Data de Execução',
      'completedAt': 'Concluído em',
      'startedAt': 'Iniciado em',
      'uf': 'UF',
      'cep': 'CEP',
      'street': 'Rua',
      'complement': 'Complemento',
      'number': 'Número',
      'attachments': 'Anexos',
      'deliveryDate': 'Data de Entrega',
      'extraInfo': 'Info Extra',
      'orderDate': 'Data do Pedido',
      'orderType': 'Tipo do Pedido',
      'paymentTerm': 'Prazo Pagamento',
      'shippingType': 'Tipo de Frete',
      'shippingValue': 'Valor do Frete',
      'taxes': 'Impostos',
      'total': 'Total',
      'trackingCode': 'Código de Rastreio',
      'totalQuantity': 'Qtd Total',
      'originDealIdPloomes': 'ID Negócio Ploomes',
      'saleNumberPloomes': 'Nº Venda Ploomes',
      'isPrincipal': 'Principal',
      'descount': 'Desconto',
      'priceKg': 'Preço/Kg',
      'priceKgMin': 'Preço/Kg Mín',
      'time': 'Tempo',
      'quantity': 'Quantidade',
      'diameter': 'Diâmetro',
      'length': 'Comprimento',
      'ploomesProductName': 'Produto Ploomes',
      'thickness': 'Espessura',
      'weight': 'Peso',
      'width': 'Largura',
      'requestedProduct': 'Produto Solicitado',
      'offeredProduct': 'Produto Oferecido',
      'deadlines': 'Prazos',
      'heatTreatments': 'Tratamentos Térmicos',
      'segment': 'Segmento',
      'price': 'Preço',
      'cost': 'Custo',
      'stock': 'Estoque',
      'category': 'Categoria',
      'type': 'Tipo',
      'unit': 'Unidade',
      'model': 'Modelo',
      'costPerHour': 'Custo/Hora',
      'capacity': 'Capacidade',
      'dimension': 'Dimensão',
      'manufacturer': 'Fabricante',
      'year': 'Ano',
      'locationId': 'ID da Localização',
      'typeId': 'ID do Tipo',
      'deadlineInDays': 'Prazo (Dias)',
      'lengthInMm': 'Comprim. (mm)',
      'stockUnit': 'Unid. Estoque',
      'totalWeight': 'Peso Total',
      'unitQuantity': 'Qtd Unitária'
    }
    
    return translations[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
  }
  
  const getIntegrationFieldsConfig = () => {
    if (!integrationConfig) return []
    
    const data = sampleData || (field as any).integrationData
    if (!data) return []
    
    const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig.fieldKey)
    const excludeKeys = ['id', fieldKey, 'tenantId', 'createdAt', 'updatedAt', 'updateAt', 'deletedAt']
    
    const fields: any[] = []


    if (data._parentRecord) {
      const parentLabel = getPrimaryLabel(data._parentRecord)
      if (parentLabel) {
        fields.push({
          key: '_parent',
          label: 'Registro',
          render: () => parentLabel,
          condition: (option: any) => !!option._parentRecord,
          getValue: (option: any) => option._parentRecord ? getPrimaryLabel(option._parentRecord) : null
        })
      }
    }
    
    const processObject = (obj: any, prefix = '') => {
      Object.keys(obj).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key
        const value = obj[key]
        
        if (key.startsWith('_') || excludeKeys.includes(key) || key.toLowerCase().includes('id')) return
        
        if (Array.isArray(value)) {
          if (value.length > 0) {
            fields.push({
              key: fullKey,
              label: translateFieldLabel(key),
              render: (val: any) => {
                if (!Array.isArray(val) || val.length === 0) return ''
                const names = val.map((v: any) => extractDisplayName(v)).filter((name) => isMeaningfulValue(name))
                if (names.length <= 3) return names.join(', ')
                return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
              },
              condition: (option: any) => {
                const keys = fullKey.split('.')
                let val: any = option
                for (const k of keys) val = val?.[k]
                if (!Array.isArray(val) || val.length === 0) return false
                const names = val.map((item: any) => extractDisplayName(item)).filter((name) => isMeaningfulValue(name))
                return names.length > 0
              },
              getValue: (option: any) => {
                const keys = fullKey.split('.')
                let val: any = option
                for (const k of keys) val = val?.[k]
                return val
              }
            })
          }
        } else if (value && typeof value === 'object') {
          processObject(value, fullKey)
        } else if (isMeaningfulValue(value)) {
          fields.push({
            key: fullKey,
            label: translateFieldLabel(key),
            render: (val: any) => {
              const displayVal = typeof val === 'object' ? val?.name || val?.companyName || val?.id || String(val) : String(val)
              if (!isMeaningfulValue(displayVal)) return ''
              
              if (key.toLowerCase() === 'cnpj') return maskCnpj(displayVal)
              if (key.toLowerCase() === 'cpf') return maskCpf(displayVal)
              if (key.toLowerCase().includes('phone') || key.toLowerCase().includes('telefone') || key.toLowerCase().includes('celular')) {
                const digits = displayVal.replace(/\D/g, '')
                if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
                if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
              }
              return displayVal
            },
            condition: (option: any) => {
              const keys = fullKey.split('.')
              let val: any = option
              for (const k of keys) {
                val = val?.[k]
              }
              return isMeaningfulValue(val)
            },
            getValue: (option: any) => {
              const keys = fullKey.split('.')
              let val: any = option
              for (const k of keys) {
                val = val?.[k]
              }
              return val
            }
          })
        }
      })
    }
    
    processObject(data)
    
    return fields.slice(0, 5)
  }

  const integrationBooleanLegend = (() => {
    if (!isIntegrated || !integrationConfig) return ''
    if (typeof integrationConfig.booleanFilterValue !== 'boolean') return ''
    return integrationConfig.booleanFilterValue ? 'Ativo' : 'Inativo'
  })()

  const fieldLabel = integrationBooleanLegend
    ? `${field.pipelineField.name} (${integrationBooleanLegend})`
    : field.pipelineField.name

  const isRequiredField = field.isRequired === true || integrationConfig?.isRequired === true
  const requiredValueDigits = (field.stringValue || '').replace(/\D/g, '')
  const isRequiredFieldSatisfied = (() => {
    if (!isRequiredField) return true
    if (isIntegrated) return integrationConfig?.integrationType === 'write' || !!field.integerValue
    if (isEmail) return !!field.stringValue && /.+@.+\..+/.test(field.stringValue)
    if (isCpf) return requiredValueDigits.length === 11
    if (isCnpj) return requiredValueDigits.length === 14
    if (isCelular) return requiredValueDigits.length >= 10 && requiredValueDigits.length <= 11
    if (isTelefone) return requiredValueDigits.length === 10
    if (isString) return !!field.stringValue && field.stringValue.trim() !== ''
    if (isDate) return !!field.dateTimeValue
    if (isCheckbox) return field.booleanValue !== undefined && field.booleanValue !== null
    if (isValor) return field.decimalValue !== undefined && field.decimalValue !== null
    return true
  })()
  const showRequiredError = !isRequiredFieldSatisfied
  const requiredErrorInputStyle: React.CSSProperties | undefined = showRequiredError
    ? { border: '2px solid #EF4444', boxShadow: '0 0 6px rgba(239, 68, 68, 0.6)' }
    : undefined

  return (
    <>
      <Label>{fieldLabel}</Label>
      {isIntegrated && integrationConfig ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <InfiniteSelect
            name={`integrated-${field.pipelineFieldId}-${integrationConfig.moduleId}-${integrationConfig.integrationType}`}
            placeholder='Selecione...'
            emptyOptionsMessage='Nenhum dado encontrado'
            errorOptionsMessage={integrationEndpointHasError ? 'Campo com erro' : undefined}
            value={null}
            onFocus={() => {
              console.log('[RenderField][integrated-focus-debug]', {
                pipelineFieldId: field.pipelineFieldId,
                fieldName: field.pipelineField.name,
                moduleId: integrationConfig.moduleId,
                moduleName: integrationConfig.moduleName,
                integrationType: integrationConfig.integrationType,
                writeRegistrationType: integrationConfig.writeRegistrationType,
                endpoint: integrationConfig.endpoint,
                createEndpoint: integrationConfig.createEndpoint,
              })
            }}
            onSelect={handleIntegrationSelect}
            fetchFunction={fetchIntegrationData}
            mode={integrationConfig.integrationType === 'write' ? 'crud' : 'readonly'}
            allowCreate={integrationConfig.integrationType === 'write' ? !canCreateRecordFromWriteIntegration : false}
            allowDelete={false}
            showSelectedBelow={false}
            getOptionLabel={(option) => {
              const fieldKey = getNormalizedIntegrationFieldKey(integrationConfig.fieldKey)
              const optionEndpoint = String(integrationConfig.endpoint || '').toLowerCase()
              const optionModuleName = String(integrationConfig.moduleName || '').toLowerCase()
              const isLegacyFabricOption =
                optionEndpoint.includes('/backend/material/list/stock') ||
                optionModuleName.includes('tecido')
              const isFabricWriteOption =
                integrationConfig.integrationType === 'write' &&
                (
                  integrationConfig.moduleId === 'fashion-fabrics-write' ||
                  integrationConfig.moduleId === 'fashion-stock' ||
                  integrationConfig.writeRegistrationType === 'fabric' ||
                  optionModuleName.includes('tecido') ||
                  optionEndpoint.includes('/stock')
                )
              const isFabricOption = isFabricWriteOption || isLegacyFabricOption

              if (option._displayName) {
                if (isFabricOption) {
                  console.log('[getOptionLabel][fabric-debug] using_display_name', {
                    id: option?.id,
                    displayName: option?._displayName,
                    color: option?.color,
                    itemColorName: option?.itemColor?.name,
                    ItemColorName: option?.ItemColor?.name,
                    pantoneCode: option?.pantoneCode || option?.itemColor?.Pantone?.code || option?.ItemColor?.Pantone?.code,
                  })
                }
                return option._displayName
              }

              if (option._parentRecord !== undefined) {
                return option._displayName || getPrimaryLabel(option)
              }
              const colValue = getDisplayValueFromField(option, fieldKey)
              if (colValue) {
                if (isFabricOption) {
                  console.log('[getOptionLabel][fabric-debug] using_col_value', {
                    id: option?.id,
                    colValue,
                    rawColor: option?.color,
                  })
                }
                console.log('[getOptionLabel] moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'colValue:', colValue, 'id:', option?.id)
                return colValue
              }
              const fallback = getPrimaryLabel(option)
              console.log('[getOptionLabel] fallback moduleId:', integrationConfig.moduleId, 'fieldKey:', fieldKey, 'fallback:', fallback, 'id:', option?.id)
              return fallback
            }}
            fieldsConfig={getIntegrationFieldsConfig()}
            showDefaultFields={true}
            hasError={showRequiredError}
            endpointConfig={integrationConfig.integrationType === 'write' && !canCreateRecordFromWriteIntegration ? {
              listPath: integrationConfig.endpoint,
              createPath: integrationConfig.createEndpoint || integrationConfig.endpoint
            } : undefined}
            showPantoneSwatch={isIntegratedColorField}
          />

          {integratedSelectedOptions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2 }}>
              {integratedSelectedOptions.map((selectedOption) => {
                const optionSwatchHex = isIntegratedColorField
                  ? (
                    selectedOption._swatchHex ||
                    getSwatchHex(selectedOption) ||
                    integratedColorSwatchMap[`id:${String(selectedOption.id)}`] ||
                    integratedColorSwatchMap[`name:${normalizeColorLabel(selectedOption.name)}`]
                  )
                  : undefined
                return (
                  <div
                    key={`${field.pipelineFieldId}-${selectedOption.id}`}
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #D9E3EE',
                      borderRadius: 18,
                      padding: '6px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: '#303030',
                    }}
                  >
                    {isIntegratedColorField && !isNumericChipLabel(selectedOption.name) && optionSwatchHex && (
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: optionSwatchHex,
                          border: '1px solid #D1D5DB',
                          display: 'inline-block',
                        }}
                      />
                    )}
                    <span>{selectedOption.name}</span>
                    <button
                      type='button'
                      onClick={() => handleRemoveIntegratedSelection(selectedOption.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#666',
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {canCreateRecordFromWriteIntegration && isFromCurrentStep && (
            <IntegratedWriteCreateModal
              isOpen={true}
              inline
              moduleType={writeRegistrationType}
              requiredFieldKeys={requiredRegistrationFieldKeys}
              optionalFieldKeys={integrationConfig.optionalRegistrationFieldKeys || []}
              draftOptions={writeRegistrationDraftOptions}
              activeDraftId={activeWriteRegistrationDraftId}
              onCreateDraft={handleCreateWriteRegistrationDraft}
              onSelectDraft={handleSelectWriteRegistrationDraft}
              stageDraft={stageDraftForInlineWriteRegistration}
              onStageDraftChange={handleStageDraftChangeFromModal}
              autoCreateWhenReady={stagedWriteRegistrationEnabled}
              autoCreateCreatedRecord={writeRegistrationAutoCreateFeedback}
              onClose={() => undefined}
              onCreated={(option: IntegratedSelectionOption) => {
                handleIntegrationSelect(option)
              }}
            />
          )}
        </div>
      ) : isCheckbox ? (
        <>
          <HiddenCheckbox checked={field.booleanValue || false} onChange={handleToggle} />
          <StyledCheckbox checked={field.booleanValue || false} onClick={handleToggle} />
        </>
      ) : isValor ? (
        <CurrencyInput
          id={`currency-${field.pipelineFieldId}`}
          name={`currency-${field.pipelineFieldId}`}
          value={field.decimalValue || 0}
          onValueChange={handleDecimalChange}
          placeholder='R$ 0,00'
          hasError={showRequiredError}
        />
      ) : isDate ? (
        <Input
          isFilled={showRequiredError ? false : undefined}
          style={requiredErrorInputStyle}
          type='date'
          placeholder='Selecione...'
          value={field.dateTimeValue ? field.dateTimeValue.split('T')[0] : ''}
          onChange={(e) => handleDateChange(e.target.value)}
        />
      ) : (
        <Input
          isFilled={showRequiredError ? false : undefined}
          style={requiredErrorInputStyle}
          type={isEmail ? 'email' : 'text'}
          placeholder={placeholder}
          value={field.stringValue || ''}
          onChange={(e) => handleStringChange(e.target.value)}
        />
      )}
    </>
  )
}

export default RenderField